import { inject, Injectable, NgZone } from "@angular/core";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { Point } from "geojson";
import { LayerService } from "../layer.service";
import { GeoService } from "../geo.service";
import { MeasurementToolService } from "../measurement-tool.service";
import { ApiViewerService } from "../api-viewer.service";
import { ViewerConstants } from "../../../../shared/model/viewer-constants";
import { Viewerlayer } from "../../../model/viewerlayer";
import { IMetadataImage } from "../../../interfaces/i-metadataimage";
import { MeasurementTool } from "../../../model/measurementtool";
import { ViewerErrorCommunicatorService } from "../../communicators/viewer-error-communicator.service";
import { FeaturesSelectedService } from "../features-selected.service";
import { ISelectedFeatures } from "../../../interfaces/i-select-features";
import { IUserConfigurationRequest } from "../../../interfaces/i-user-configuration-request";
import { UserConfigCommunicatorService } from "../../communicators/user-config-communicator.service";
import { ViewerConfigurationService } from "../../viewer-configuration.service";
import { ToolbarService } from "../toolbar.service";
import { IHistoryProject } from "../../../interfaces/i-history-project";
import { ImagesService } from "../images.service";
import { LoadingService } from "../loading.service";
import { GeoJSONDatabaseService } from "../geojson-database.service";
import { IPopup } from "../../../interfaces/i-popup";
import { IPropertyResponse } from "../../../interfaces/i-property-response";
import { IDamageAssessmentResponse } from "../../../interfaces/i-damage-assessment-response";
import { WindowMessageService } from "../../communicators/window-message.service";
import { LayerUtilsService } from "../layer-utils.service";
import { MapStatus } from "../../../model/map-status";
import { ProjectStatus } from "../../../model/project-status";
import {
  GoogleAnalyticsConstantsCategory,
  GoogleAnalyticsConstantsEvents,
} from "../../../../shared/model/google-analytics-constants";
import { IProjectAOI } from "../../../interfaces/i-project-aoi";
import { ITimelineFilter } from "../../../interfaces/i-timeline-filter";
import { toolbarOptions } from "../../../../shared/model/toolbar-constants";
import { ProjectService } from "../project.service";
import { FilterProjectService } from "../filter-project.service";
import { UrlStateService } from "../url-state.service";
import { MapService } from "../map.service";
import { ReportService } from "../report.service";
import { take, tap } from "rxjs/operators";
import { StatusService } from "../status.service";
import { ElementsService } from "../elements.service";
import { ICollection } from "../../../interfaces/i-collection";
import moment from "moment";
import { GoogleAnalyticsService } from "../../../../shared/services/google-analytics.service";
import { VlmLayerPanelService } from "../../../../shared-components/vlm-layer-panel/vlm-layer-panel.service";
import { PipelineEmbeddingsPanelService } from "../../../../shared-components/pipeline-embeddings-panel/pipeline-embeddings-panel.service";
import { PipelineAoiPanelService } from "../../../../shared-components/pipeline-aoi-panel/pipeline-aoi-panel.service";
import { FeatureDetailPanelService } from "../../../../shared-components/feature-detail-panel/feature-detail-panel.service";
import { AiLayerStyles } from "../../../../ai-components/chat/services/ai-layer-styles";
import { GeoTiffRendererService } from "../../../../ai-components/chat/services/geo-tiff-renderer.service";
import { EvidenceLayerUpdateRequest } from "../../../../ai-components/chat/model/evidence-layer.types";
import { LayerSource } from "../../../model/layersource";
import GeoJSON from "ol/format/GeoJSON";
import { FeatureFlagsService } from "../feature-flags.service";
import { FeatureFlags } from "../../../model/feature-flags";
import { IStyleLegendMetadata, IVectorStyle } from "../../../../shared/model/vector-style-definition.interface";
import { convertVectorStyle } from "../../../../shared/utils/geojson-style-converter";
import { ToastService } from "../toast.service";
import { TOAST_STATUS } from "../../../model/ToastConstants";

/** Returns a shallow clone of the layer tree (self + childs, recursively) with `styleFunction` omitted — required before any IndexedDB write, since live style functions aren't structured-cloneable. */
export function stripStyleFunctions(layer: Viewerlayer): Viewerlayer {
  const { styleFunction, childs, ...rest } = layer as any;
  return childs ? { ...rest, childs: childs.map(stripStyleFunctions) } : rest;
}

/** Request for `addEvidenceLayer` — a prepared collection plus optional display name, named style function and stable group descriptor. */
export interface EvidenceLayerAddRequest {
  /** Prepared FeatureCollection (FileReaderService.prepareGeoJson output). */
  collection: any;

  /** Display name; falls back to the collection's `properties.name` when absent. */
  name?: string;

  /** Named OL style function resolved by `applyStyleFunctions`; an embedded `.style` definition wins. */
  styleFunctionName: string;

  /** Stable group descriptor (upserted via `ensureLayerGroup`) when the layer joins a group. */
  group?: { key: string; name?: string };
}

/** Deterministic fallback display name for a stable group key: the part after the first ":" (e.g. "plan:<plan_ref>"), the key itself otherwise. */
function fallbackGroupDisplayName(key: string): string {
  const separatorIndex = key.indexOf(":");
  const tail = separatorIndex >= 0 ? key.slice(separatorIndex + 1) : key;
  return tail.length > 0 ? tail : key;
}

/**
 * Base class to viewer services
 * Each viewer have to implement this base service
 * Launch events with observables
 * Store status of the viewer
 * Other components can listen this observables
 */
@Injectable()
export abstract class BaseViewerService {
  protected googleAnalyticsService = inject(GoogleAnalyticsService);

  protected vlmLayerPanelService = inject(VlmLayerPanelService);

  protected pipelineEmbeddingsPanelService = inject(PipelineEmbeddingsPanelService);

  protected pipelineAoiPanelService = inject(PipelineAoiPanelService);

  protected featureDetailPanelService = inject(FeatureDetailPanelService);

  protected featureFlagService = inject(FeatureFlagsService);

  protected featuresService = inject(FeaturesSelectedService);

  protected toolbarService = inject(ToolbarService);

  protected loadingService = inject(LoadingService);

  protected apiViewerService = inject(ApiViewerService);

  protected geoJSONDatabaseService = inject(GeoJSONDatabaseService);

  protected geoTiffRendererService = inject(GeoTiffRendererService);

  protected windowMessageService = inject(WindowMessageService);

  protected ngZone = inject(NgZone);

  protected urlStateService = inject(UrlStateService);

  protected mapService = inject(MapService);

  protected toastService = inject(ToastService);

  protected reportService = inject(ReportService);

  protected elementsService = inject(ElementsService);

  protected layerService = inject(LayerService);

  protected measurementsToolService = inject(MeasurementToolService);

  protected viewerConfigurationService = inject(ViewerConfigurationService);

  protected userConfigCommunicatorService = inject(UserConfigCommunicatorService);

  protected viewerErrorCommunicatorService = inject(ViewerErrorCommunicatorService);

  // region Subjects definition
  protected selectedBaseLayerSubject = new BehaviorSubject<Viewerlayer>(null);

  public selectedBaseLayer$ = this.selectedBaseLayerSubject.asObservable();

  protected baseLayersSubject = new BehaviorSubject<Viewerlayer[]>([]);

  public baseLayers$ = this.baseLayersSubject.asObservable();

  protected overlayLayersSubject = new BehaviorSubject<Viewerlayer[]>([]);

  public overlayLayers$ = this.overlayLayersSubject.asObservable();

  protected initLayersSubject = new BehaviorSubject<Viewerlayer[]>([]);

  protected userLayersSubject = new BehaviorSubject<Viewerlayer[]>([]);

  public userLayers$ = this.userLayersSubject.asObservable();

  protected collectionLayersSubject = new BehaviorSubject<Viewerlayer[]>([]);

  public collectionLayers$ = this.collectionLayersSubject.asObservable();

  protected firstLoadSubject = new BehaviorSubject<boolean>(false);

  public firstLoad$ = this.firstLoadSubject.asObservable();

  protected loadedImagesSubject = new BehaviorSubject<IMetadataImage[]>([]);

  public loadedImages$ = this.loadedImagesSubject.asObservable();

  protected loadedProjectsSubject = new BehaviorSubject<IHistoryProject[]>(null);

  public loadedProjects$ = this.loadedProjectsSubject.asObservable();

  protected imageSubject = new BehaviorSubject<IMetadataImage>(null);

  public image$ = this.imageSubject.asObservable();

  protected lockImageModeSubject = new Subject<boolean>();

  public lockImageMode$ = this.lockImageModeSubject.asObservable();

  protected enableImageInfoSubject = new Subject<boolean>();

  public enableImageInfo$ = this.enableImageInfoSubject.asObservable();

  protected measurementToolsSubject = new BehaviorSubject<MeasurementTool[]>([]);

  public measurementTools$ = this.measurementToolsSubject.asObservable();

  protected developerToolsSubject = new BehaviorSubject<boolean>(false);

  public developerTools$ = this.developerToolsSubject.asObservable();

  protected enableUploadVectorTilesSubject = new BehaviorSubject<boolean>(false);

  public enableUploadVectorTiles$ = this.enableUploadVectorTilesSubject.asObservable();

  public enableExportGeoTIFFSubject = new BehaviorSubject<boolean>(false);

  public enableExportGeoTIFF$ = this.enableExportGeoTIFFSubject.asObservable();

  protected enableAdditionalImagerySubject = new BehaviorSubject<boolean>(true);

  protected enableMeasurementsSubject = new Subject<boolean>();

  public enableMeasurements$ = this.enableMeasurementsSubject.asObservable();

  public enablePropertiesSubject = new BehaviorSubject<boolean>(false);

  public enableProperties$ = this.enablePropertiesSubject.asObservable();

  public enablePropertiesReportSubject = new BehaviorSubject<boolean>(false);

  public enablePropertiesReport$ = this.enablePropertiesReportSubject.asObservable();

  protected mapCenterCoordinatesSubject = new BehaviorSubject<number[]>([]);

  public mapCenterCoordinates$ = this.mapCenterCoordinatesSubject.asObservable();

  protected deactivateToolsSubject = new Subject<boolean>();

  public deactivateTools$ = this.deactivateToolsSubject.asObservable();

  protected zoomEndSubject = new BehaviorSubject<any>(null);

  public zoomEnd$ = this.zoomEndSubject.asObservable();

  protected searchEndSubject = new BehaviorSubject<any>(null);

  public searchEnd$ = this.searchEndSubject.asObservable();

  protected infoPopupSubject = new BehaviorSubject<IPopup>(null);

  public infoPopup$ = this.infoPopupSubject.asObservable();

  protected updateLocationInfoSubject = new Subject<IPopup>();

  public updateLocationInfo$ = this.updateLocationInfoSubject.asObservable();

  public pointInfoPopupSubject = new BehaviorSubject<IPopup>(null);

  public pointInfoPopup$ = this.pointInfoPopupSubject.asObservable();

  protected updatePointInfoPopupSubject = new Subject<IPopup>();

  public updatePointInfoPopup$ = this.updatePointInfoPopupSubject.asObservable();

  protected measuresSelectedSubject = new BehaviorSubject<any>(null);

  public measuresSelected$ = this.measuresSelectedSubject.asObservable();

  public resizeInfoMessageSubject = new BehaviorSubject<boolean>(false);

  public resizeInfoMessage$ = this.resizeInfoMessageSubject.asObservable();

  protected dsmStatusSubject = new BehaviorSubject<any>({
    enable: false,
    status: "stop",
    inside: false,
    coverage: [],
  });

  public dsmStatus$ = this.dsmStatusSubject.asObservable();

  private measurementSystemIdSubject = new BehaviorSubject<number>(ViewerConstants.MEASUREMENT_SYSTEMS.IDS.METRIC);

  public measurementSystemId$ = this.measurementSystemIdSubject.asObservable();

  private measurementLengthUnitSubject = new BehaviorSubject<string>(null);

  public measurementLengthUnit$ = this.measurementLengthUnitSubject.asObservable();

  private measurementAreaUnitSubject = new BehaviorSubject<string>(null);

  public measurementAreaUnit$ = this.measurementAreaUnitSubject.asObservable();

  private measurementSlopeUnitSubject = new BehaviorSubject<string>(null);

  public globalMeasurementSystem$ = this.viewerConfigurationService.globalMeasurementSystem$;

  private propertyInfoUnitSubject = new BehaviorSubject<number>(null);

  public propertyInfoUnit$ = this.propertyInfoUnitSubject.asObservable();

  protected relativeElevationStatusSubject = new Subject<[any]>();

  public relativeElevationStatus$ = this.relativeElevationStatusSubject.asObservable();

  protected centerImageDateSubject = new BehaviorSubject<string>(null);

  public centerImageDate$ = this.centerImageDateSubject.asObservable();

  protected selectedFeaturesSubject = new BehaviorSubject<[any]>(null);

  public selectedFeatures$ = this.selectedFeaturesSubject.asObservable();

  protected projectStatusSubject = new BehaviorSubject<ProjectStatus>(null);

  public projectStatus$ = this.projectStatusSubject.asObservable();

  protected projectStatusTriggerSubject = new Subject<ProjectStatus>();

  public projectStatusTrigger$ = this.projectStatusTriggerSubject.asObservable();

  protected exportFeatureSubject = new BehaviorSubject<[any]>(null);

  public exportFeature$ = this.exportFeatureSubject.asObservable();

  protected reportReadySubject = new BehaviorSubject<boolean>(false);

  public reportReady$ = this.reportReadySubject.asObservable();

  protected timelineFiltersSubject = new BehaviorSubject<ITimelineFilter>(null);

  protected readNotificationsSubject = new BehaviorSubject<string[]>(null);

  protected isActivatedBaseMapSubject = new BehaviorSubject<boolean>(false);

  public isActivatedBaseMap$ = this.isActivatedBaseMapSubject.asObservable();

  private _orientationSelected = ViewerConstants.COLLECTION_MODES.VERTICAL.orientations.N.name;

  private _collectionMode = ViewerConstants.COLLECTION_MODES.VERTICAL;

  protected selectedAoiSubject = new BehaviorSubject<IProjectAOI>(null);

  protected projectsPopulatedSubject = new BehaviorSubject<boolean>(false);

  public projectsPopulated$ = this.projectsPopulatedSubject.asObservable();

  // endregion

  // region Variable definition
  protected baseLayersRaw: Viewerlayer[] = [];

  protected overlayLayersRaw: Viewerlayer[] = [];

  protected collectionLayersRaw: Viewerlayer[] = [];

  protected userLayersRaw: Viewerlayer[] = [];

  protected selectedBaseLayerRaw: Viewerlayer;

  protected infoPropertiesRaw: IPopup[] = [];

  private _instanceId: number;

  public collectionProjects: Viewerlayer[];

  public collectionsPopulated = false;

  public initAoi: string;

  // endregion

  // region Collection Service subjects

  protected infraredStatusSubject = new BehaviorSubject<boolean>(false);

  public infraredStatus$ = this.infraredStatusSubject.asObservable();

  protected enableContrastSubject = new BehaviorSubject<boolean>(false);

  public enableContrast$ = this.enableContrastSubject.asObservable();

  protected lockCollectionSubject = new Subject<boolean>();

  public lockCollection$ = this.lockCollectionSubject.asObservable();

  protected enableSwipeSubject = new Subject<boolean>();

  public enableSwipe$ = this.enableSwipeSubject.asObservable();

  protected enableDualViewSubject = new Subject<any>();

  public enableDualView$ = this.enableDualViewSubject.asObservable();

  protected infraredTriggerSubject = new Subject<boolean>();

  public infraredTrigger$ = this.infraredTriggerSubject.asObservable();

  public infoPropertiesSubject = new BehaviorSubject<IPopup[]>(null);

  public infoProperties$ = this.infoPropertiesSubject.asObservable();

  public apiService: any;

  // endregion Collection Service subjects

  // region Constructor // Initialization

  public readonly instanceId: string;

  constructor() {
    this.instanceId = `MyService-${crypto.randomUUID()}`;
    this.viewerConfigurationService.globalMeasurementSystem$.subscribe((systemId) => {
      this.applyMeasurementSystemInternally(systemId);
    });
  }

  // Collection Service
  addCollectionLayers(layers: Viewerlayer[]): void {
    this.updateCollectionLayers(layers);

    if (!this.getCoreMap()) {
      return;
    }
    const coreMap = this.getCoreMap();
    coreMap.addListener("mode:view", this.onChangeModeView.bind(this));
    coreMap.addListener("orientation:view", this.onChangeOrientation.bind(this));
    coreMap.addListener("nodiscreteimages", this.onObliqueNoData.bind(this));
    coreMap.addListener("extentoutsideimage", this.onExtentOutsideImage.bind(this));
    coreMap.addListener("discrete:error_loading", this.onObliqueErrorLoading.bind(this));
    coreMap.addListener("collection:lock", this.onLockCollection.bind(this));
  }

  onLockCollection(event) {
    this.lockCollectionSubject.next(event.lock);
  }

  onChangeModeView(evt: any): Observable<any> {
    if (this.collectionMode !== evt.mode) {
      this.collectionMode = evt.mode;
      // change orientation to north when change mode to vertical
      if (
        evt.mode.name === ViewerConstants.COLLECTION_MODES.VERTICAL.name &&
        this.orientationSelected !== ViewerConstants.COLLECTION_MODES.VERTICAL.orientations.N.name
      ) {
        this.orientationSelected = ViewerConstants.COLLECTION_MODES.VERTICAL.orientations.N.name;
      }
    }

    this.updateCenterImageDate();

    return of(this.collectionMode);
  }

  onExtentOutsideImage(event: any): void {
    this.changeTo2D(null, event.epsg, event.requestedExtent);
  }

  onObliqueNoData(event: any): void {
    this.projectStatusTriggerSubject.pipe(take(1)).subscribe((status) => {
      // this.handleObliqueNoDataEvent(event, status);
    });
  }

  // TODO: Failing when extent outside image and no data at the same time
  /*handleObliqueNoDataEvent(event: any, status: ProjectStatus = null) {
    if (!event.layer || !event.requestedPoint) {
      this.changeTo2D(event.requestedPoint, event.epsg);
      return;
    }
    const projectStatus = status ?? this.getProjectStatus();
    let aois = this.mapService
      .getCollectionManager()
      .findCollectionsByLocation({ latitude: event.requestedPoint[0], longitude: event.requestedPoint[1] }, event.epsg)
      .map((collection) => projectStatus.findImageAOI(collection.properties.collection))
      .filter((aoi) => aoi.isVisible() && aoi.hasNonArchivedOrientation(event.orientation));

    if (!aois?.length) {
      // change to new point after change mode
      this.changeTo2D(event.requestedPoint, event.epsg);
      return;
    }

    // check if we have some aoi for the same layer
    if (aois.some((aoi) => aoi.getLayer() === event.collectionType)) {
      aois = aois.filter((aoi) => aoi.getLayer() === event.collectionType);
    }
    const aoi = ProjectService.visibleProject(aois);

    if (aoi) {
      this.show3DProject(aoi, null, event.requestedPoint, event.epsg);
      return;
    }
    // we should not launch this never
    this.changeTo2D(event.requestedPoint, event.epsg);
  }*/

  onChangeOrientation(evt: any) {
    if (this.orientationSelected !== evt.orientation) {
      this.orientationSelected = evt.orientation;
    }
    if (this.viewerConfigurationService.getDefaultLocationValue()) {
      this.mapService
        .getCoreMap()
        .fitToExtent(GeoService.bbox(this.getCoreMap().getDefaultLocation()), ViewerConstants.PROJECTIONS.WGS84);
      this.viewerConfigurationService.setDefaultLocation(false);
    }
  }

  onObliqueErrorLoading(): void {
    this.viewerErrorCommunicatorService.setViewerError(
      "Something is wrong when i try to load image for this layer at this point.",
    );
    this.changeTo2D();
  }

  changeOrientation(orientation: string) {
    const { centerAOI } = this.getProjectStatus() || null;
    if (centerAOI && this.collectionMode.value === ViewerConstants.COLLECTION_MODES.OBLIQUE.value) {
      const collectionId = centerAOI.getName() ? centerAOI.getCollectionId() : centerAOI.getLayerId();
      const projectCode = centerAOI.getName();
      this.closePointInfoPopups();
      this.getCoreMap().changeCollectionOrientation(collectionId, orientation, projectCode);
    } else {
      this.rotateView(orientation);
    }
    this.orientationSelected = orientation;
  }

  changeMode() {
    this.clearContextMenu();
    if (this.is3DMode()) {
      this.changeTo2D();
      this.googleAnalyticsService.setGoogleAnalyticsEvent(
        GoogleAnalyticsConstantsEvents.CHANGE_MODE + ViewerConstants.COLLECTION_MODES.VERTICAL.name,
        GoogleAnalyticsConstantsCategory.COLLECTION_CONTROLLER,
        true,
      );
    } else {
      this.show3DProject(this.getProjectStatus().centerAOI);
      this.googleAnalyticsService.setGoogleAnalyticsEvent(
        GoogleAnalyticsConstantsEvents.CHANGE_MODE + ViewerConstants.COLLECTION_MODES.OBLIQUE.name,
        GoogleAnalyticsConstantsCategory.COLLECTION_CONTROLLER,
        true,
      );
    }
    this.enableContextMenu();
  }

  changeTo2D(point: Point = null, epsg: string = null, extent: number[] = null) {
    if (point) {
      // we can force a location after change mode
      const forceCenter = () => {
        const coordinates = point as unknown as number[];
        this.mapService.setCenter(coordinates, epsg || undefined);
        this.getCoreMap().removeEventListener("mode:view", forceCenter);
      };
      this.getCoreMap().addEventListener("mode:view", forceCenter);
    }
    if (extent) {
      // we can force a location after change mode
      const forceCenter = () => {
        this.mapService.fitToExtent(extent, epsg || undefined);
        this.getCoreMap().removeEventListener("mode:view", forceCenter);
      };
      this.getCoreMap().addEventListener("mode:view", forceCenter);
    }

    this.getCoreMap().changeCollectionMode(ViewerConstants.COLLECTION_MODES.VERTICAL.value);
    this.collectionMode = ViewerConstants.COLLECTION_MODES.VERTICAL;
    this.getCoreMap().changePropertyFootprintLayerVisibility(true);
    this.closePointInfoPopups();
    this.mapService.setDiscreteEnabled(false);
    if (this.viewerConfigurationService.isInitialized()) {
      const overlayLayersVisible = this.getOverlayLayers().filter((layer) => layer.visible);
      overlayLayersVisible.forEach((layer) => {
        this.changeLayerVisibility(layer.id, true);
      });
    }
  }

  show3DImage(image: IMetadataImage) {
    const aoi = ProjectService.imageProject(this.getProjectStatus().enabledAOIs, image);
    if (aoi) {
      this.show3DProject(aoi, image);
    }
  }

  getVisibleDateTimeForPoint(point: number[]): string {
    // just to return year enough to get max date for collection
    const visible = this.mapService.findVisibleCollection(GeoService.toMercator(point)) as ICollection;
    if (visible) {
      return moment(visible.properties["last-capture-date"]).format("YYYY") || null;
    }
    return null;
  }

  getAllAvailableImages(geographicPoint: any): Observable<any> {
    const layers = this.mapService.getOverlayManager().getVexcelSources();
    return this.mapService.findImagesInPoint(geographicPoint, ViewerConstants.GEOGRAPHIC_CODE, layers);
  }

  /**
   * Show timeline if not in 3d mode and the zoom level is higher than expected
   */
  show2DTimeline(): boolean {
    return !this.is3DMode() && this.getZoom() >= ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL;
  }

  showDummyTimeline(): boolean {
    return !this.is3DMode() && this.getZoom() < ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL;
  }

  handleState(state: any) {
    if (!state) {
      return;
    }
    // FIRST we change the orientation, because this affect to the change mode
    /*if (this.orientationSelected !== state.orientation) {
                              this.changeOrientation(state.orientation);
                            }*/

    // SECOND after load aois we can change the mod
    const projectStatus = this.getProjectStatus();
    /*if (this.initAoi) {
                              this.handleEnabledAoi(projectStatus, this.initAoi, state.mode);
                            }*/

    // THIRD: Now we can change the infrared status, in
    if (state.nir && state.mode === ViewerConstants.COLLECTION_MODES.VERTICAL.name) {
      this.infraredTriggerSubject.next(state.nir);
    }

    // LAST:  we change to swipe mode or dual view, are excluyents so first check swipe and then dual view
    if (projectStatus && state.swipe && this.mapService.isMasterMap()) {
      const swipeStatus = { enable: true } as any;
      swipeStatus.left = projectStatus.aois.find((aoi) => aoi.getLayerId().toString() === state.swipe.left);
      swipeStatus.right = projectStatus.aois.find((aoi) => aoi.getLayerId().toString() === state.swipe.right);
      this.enableSwipeSubject.next(swipeStatus);
    } else if (this.mapService.isMasterMap() && state.dual) {
      this.enableDualViewSubject.next(state.dual);
    }

    if (state.measurementSystem !== undefined) {
      this.setGlobalMeasurementSystem(state.measurementSystem);
    }
  }

  handleEnabledAoi(projectStatus: ProjectStatus, enabledAoi: string, mode: string) {
    const existAOI =
      enabledAoi && projectStatus?.enabledAOIs
        ? projectStatus.enabledAOIs.find((aoi) => aoi.getName() === enabledAoi)
        : null;
    if (existAOI && mode === ViewerConstants.COLLECTION_MODES.OBLIQUE.name) {
      this.show3DProject(existAOI);
    }
  }

  /**
   * Get coremap info
   */
  getConfigurationData(callback: (result: any) => any): void {
    return this.getCoreMap().getConfigurationInfo((response) => {
      if (response.ready) {
        callback(response.initConfig);
      }
    });
  }

  // endregion

  // region Developer tools
  /**
   * Toggle developer tools
   */
  toggleDeveloperTools() {
    this.developerToolsSubject.next(!this.developerToolsSubject.value);
  }

  enableDebugPanel(enable: boolean) {
    const debugParameters = this.getCoreMap().getDebugParameters();
    debugParameters.showStatusControl = enable;
    this.getCoreMap().setDebugParameters(debugParameters);
  }

  enableImageInfo(enable: boolean) {
    this.enableImageInfoSubject.next(enable);
  }

  enableFeaturesPopup(enable: boolean) {
    const debugParameters = this.getCoreMap().getDebugParameters();
    debugParameters.enableFeaturesPopup = enable;
    this.getCoreMap().setDebugParameters(debugParameters);
  }

  enableFootprints(enable: boolean, orientation: string) {
    const debugParameters = this.getCoreMap().getDebugParameters();
    debugParameters.paintFootprints = enable;
    debugParameters.footprintsOrientation = orientation;
    this.getCoreMap().setDebugParameters(debugParameters);
  }

  enableDebugPaintCenter(enable: boolean) {
    const debugParameters = this.getCoreMap().getDebugParameters();
    debugParameters.paintCenter = enable;
    this.getCoreMap().setDebugParameters(debugParameters);
  }

  enableDebugLayerControl(enable: boolean) {
    const debugParameters = this.getCoreMap().getDebugParameters();
    debugParameters.showLayerControl = enable;
    this.getCoreMap().setDebugParameters(debugParameters);
  }

  enableDebugPaintCoverage(enable: boolean) {
    const debugParameters = this.getCoreMap().getDebugParameters();
    debugParameters.paintCoverages = enable;
    this.getCoreMap().setDebugParameters(debugParameters);
  }

  enableDebugPaintGrid(enable: boolean) {
    const debugParameters = this.getCoreMap().getDebugParameters();
    debugParameters.paintTileGrid = enable;
    this.getCoreMap().setDebugParameters(debugParameters);
  }

  // endregion
  // region Processing / collections
  /**
   * Fetch project data from parents
   * Just run first time
   * @param layer
   */
  fetchCollectionProjectData(layer: Viewerlayer): Viewerlayer[] {
    return layer.aois?.map((aoi) => {
      aoi.collection_name = layer.name;
      aoi.collection_visible = layer.visible;
      aoi.collectionType = layer.collectionType;
      aoi.aois?.map((child) => {
        child.parent_pretty_name = aoi.pretty_name;
        child.collection_name = layer.name;
        child.collection_visible = layer.visible;
        child.collectionType = layer.collectionType;
        child.parent_visible = aoi.visible;
      });
      return aoi;
    });
  }

  /**
   * Create image projects with list of images
   *
   * @param images
   * @param projectsEnabled list of project names enabled
   */
  populateCollectionProjects(images: IMetadataImage[], projectsEnabled: string[]) {
    // create image projects, and populate projects (a.k.a) with this (IHistoryProject)
    let imageProjects = images?.length ? this.updateLoadedImages(images) : [];

    // If we have collection layers do the old logi for getting the collection projects
    // todo remove this code on future refactor.
    if (this.getCollections()?.length > 0) {
      const projects = [];
      const collections = this.mapService.getCollectionManager().getCollections() as ICollection[];
      for (const info of collections) {
        try {
          const imageProject = imageProjects.find((ip) => ip.code === info.properties.collection);
          const enabled = projectsEnabled?.includes(info.properties.collection) ?? false; // check if enabled project
          const collection = ProjectService.processCollection(imageProject, info, enabled);
          projects.push(collection);
        } catch (err) {
          console.log("Error processing collection info");
        }
      }
      // return projects;
      // force to refresh projects in collection, if its empty remove the previous image projects
      ProjectService.populateImageProjects(this.getCollections(), imageProjects);
      // calculate projects from aois images order blabla
      return this.getCollectionProjectLayers().map((vl) => {
        const enabled = projectsEnabled?.includes(vl.aoi) ?? false;
        return ProjectService.processProject(vl, enabled);
      });
    } else {
      // Get all collection from collectionManager and then update collection information
      // with imageProjects information
      const projects = [];
      const collections = this.mapService.getCollectionManager().getCollections() as ICollection[];
      for (const info of collections) {
        try {
          const imageProject = imageProjects.find((ip) => ip.code === info.properties.collection);
          const enabled = projectsEnabled?.includes(info.properties.collection) ?? false; // check if enabled project
          const collection = ProjectService.processCollection(imageProject, info, enabled);
          projects.push(collection);
        } catch (err) {
          console.log("Error processing collection info");
        }
      }
      return projects;
    }
  }

  /**
   * Plain all collections projects in one array
   * @deprecated
   */
  getCollectionProjectLayers(): Viewerlayer[] {
    if (!this.collectionProjects?.length) {
      this.collectionProjects = this.getCollections()
        .filter((collection) => collection.aois)
        .reduce((previousValue, currentValue) => previousValue.concat(currentValue.aois), [])
        .reduce(
          (previousValue, currentValue) =>
            currentValue.aois?.length ? previousValue.concat(currentValue.aois) : previousValue.concat([currentValue]),

          [],
        );
    } else {
      //This part of the code it´s for set collectionProjects from new collection manager for VexcelMulti layer
    }

    return this.collectionProjects;
  }

  // endregion

  setImageDiscrete(image: IMetadataImage) {
    this.imageSubject.next(image);
    this.updateLocationInfo();
  }

  isPointToolActive() {
    return this.getMeasurementTools()
      .filter((tool) => tool.active)
      .find((t) => t.id === 21 || t.id === 22);
  }

  // region Handle events

  zoomEnd(evt: any) {
    this.zoomEndSubject.next(evt);
    this.setCenterCoordinates(evt.coordinates);
  }

  searchEnd(evt: any) {
    this.searchEndSubject.next(evt);
  }

  /**
   * Function to handle when pointer is out of limits of discrete image
   * @param evt
   */
  outImageLimits(evt: any): void {
    if (this.enableAdditionalImagerySubject.getValue() !== evt.outOfLimits) {
      this.enableAdditionalImagerySubject.next(evt.outOfLimits);
    }
  }

  startTilesLoading(): void {
    this.showLoadingComponent();
  }

  endTilesLoading(): void {
    this.hideLoadingComponent();
  }

  /**
   * Handle event when map moves
   * @param evt map center coordinates
   * @deprecated //todo should we remove this?? wait until collection status update?
   */
  updateOnMove(evt: any): void {
    if (evt.coordinates) {
      this.setCenterCoordinates(evt.coordinates);
    }
  }

  updateAfterMoveEnd() {
    if (!this.getCoreMap() && this.collectionsPopulated) {
      return;
    }
    const mapStatus = this.getCoreMap().getMapStatus(ViewerConstants.PROJECTIONS.WebMercator);
    if (mapStatus && mapStatus.center && !(Number.isNaN(mapStatus.center[0]) || Number.isNaN(mapStatus.center[1]))) {
      this.updateUrlParameters(false);
    }
  }

  mapFeaturesClicked(event: ISelectedFeatures): void {
    this.getCoreMap().removeOverlayPopUp("map-features");

    const features = event?.features;
    if (!features?.length) {
      return;
    }

    const topFeature = features[0];

    this.ngZone.run(() => {
      if (!this.isPointToolActive()) {
        this.routeFeatureClick(topFeature, event);
      }
    });
  }

  private routeFeatureClick(topFeature: any, event: ISelectedFeatures): void {
    if (topFeature.geometry?.type === "Point" && topFeature.properties?.type === "measurement") {
      this.showPointInfoPopup(topFeature.geometry.coordinates, topFeature, ViewerConstants.PROJECTIONS.WGS84);
      return;
    }

    const pipelineGroup: string = topFeature.properties?._pipeline_layer_group;
    if (pipelineGroup === "geofence") return;
    if (pipelineGroup === "aoi" && topFeature.properties?.type === "zone") return;

    if (["properties", "damage", "vlm", "embeddings", "aoi"].includes(pipelineGroup)) {
      this.showPipelineFeatureInfo(topFeature);
      return;
    }
    if (this.featureFlagService.hasFlag(FeatureFlags.AI_AGENTS)) {
      this.openFeatureDetailPanelForClick(topFeature, pipelineGroup);
    } else {
      this.featuresService.setFeaturesClicked({ ...event, features: [topFeature] });
    }
  }

  /**
   * Opens the property/damage sidebar panel for a clicked feature from a pipeline group layer.
   * Data is taken directly from feature.properties (already normalized at layer-add time),
   * so no transformation is needed here.
   */
  private showPipelineFeatureInfo(feature: any): void {
    const group: string = feature.properties._pipeline_layer_group;

    if (group === "vlm") {
      this.vlmLayerPanelService.openFeatureDetail(feature);
      return;
    }

    if (group === "embeddings") {
      this.pipelineEmbeddingsPanelService.openFeatureDetail(feature);
      return;
    }

    if (group === "aoi") {
      this.pipelineAoiPanelService.openFeatureDetail(feature);
      return;
    }

    // Use the WGS84 centroid pre-computed at layer-add time (stored in _pipeline_centroid).
    // The geometry from the OL click event is not in standard GeoJSON format so Turf cannot compute it live.
    const coords: number[] = (feature.properties._pipeline_centroid as number[]) ?? null;

    const popup: IPopup = {
      coordinates: coords,
      epsg: ViewerConstants.PROJECTIONS.WGS84,
      type: "info-popup",
      selected: true,
      onClose: this.closeAddressPopup.bind(this),
      opts: {
        icon: {
          src: "assets/images/small_pin.svg",
          color: this.getPinColor(),
        },
      },
    };

    if (group === "properties") {
      popup.property = feature.properties as IPropertyResponse;
    } else if (group === "damage") {
      popup.damageAssessment = feature.properties as IDamageAssessmentResponse;
    }

    this.infoPropertiesRaw.forEach((info) => {
      info.selected = false;
    });
    this.infoPropertiesRaw.unshift(popup);
    this.setInfoProperties(this.infoPropertiesRaw);
  }

  /**
   * Debug-mode (Alt+0) entry point: opens the feature-detail panel (rotation always; property
   * editing only for plain GeoJSON user layers, never for pipeline-tagged layers) instead of the
   * normal click routing. Rotation is always scoped to the single layer containing topFeature —
   * never across sibling layers. Returns false if no containing layer could be resolved, so the
   * caller falls back to normal routing.
   *
   * Pipeline layers (vlm/embeddings/aoi/damage/properties) are added via addGroupLayer() as real
   * Viewerlayer children (tagged with `layerGroup`), stored in the same userLayersRaw/getUserLayers()
   * state as plain GeoJSON user layers — so this single lookup covers both cases.
   */
  private openFeatureDetailPanelForClick(topFeature: any, pipelineGroup: string | undefined): boolean {
    const userLayer = LayerUtilsService.flattenWithChildren(this.getUserLayers()).find(
      (l) => l.id === topFeature.properties?.layerId,
    );

    if (userLayer) {
      const features = userLayer.source?.features?.features ?? [];
      const startIndex = features.findIndex((f) => f.properties.id === topFeature.properties.id);
      this.featureDetailPanelService.open({
        layerName: userLayer.name,
        features,
        startIndex: startIndex >= 0 ? startIndex : 0,
        sourceEpsg: 3857,
        onUpdateProperties: userLayer.layerGroup ? undefined : () => this.updateUserLayer(userLayer),
      });
      return true;
    }

    if (pipelineGroup) {
      // Defensive fallback for pipeline features whose layer isn't (yet) resolvable via
      // getUserLayers() — e.g. added through a path other than addGroupLayer(). Best-effort:
      // reuse the vlm/embeddings grid registry if the user opened it earlier this session,
      // otherwise show just the clicked feature (rotation is then a no-op, not a bug).
      const registered =
        pipelineGroup === "vlm"
          ? this.vlmLayerPanelService.registeredLayer()
          : pipelineGroup === "embeddings"
          ? this.pipelineEmbeddingsPanelService.registeredLayer()
          : null;
      const features = registered?.features ?? [topFeature];
      const startIndex = registered ? Math.max(features.indexOf(topFeature), 0) : 0;
      this.featureDetailPanelService.open({
        layerName: registered?.layerName ?? pipelineGroup,
        features,
        startIndex,
      });
      return true;
    }

    return false;
  }

  /**
     * Event launched when receive measure selected
     the first time
     */
  onMeasuresSelected(event: any): void {
    if (event) {
      this.measuresSelectedSubject.next(event.selected);
    }
  }

  /**
   * Event launched when measure is updated
   *
   */
  onMeasureUpdated(event: any): void {
    if (event) {
      if (event.feature.getProperties().measurement.location) {
        this.updatePointInfoPopup(event.feature.getProperties().measurement.location.value, event.feature);
      }
    }
  }

  /**
   * Event launched when measure is finished
   *
   */
  onMeasureFinished(event: any): void {
    let name;
    if (!event.tooltips) {
      name = GoogleAnalyticsConstantsEvents.DRAWING_ONLY_COMPLETE;
    } else {
      name = event.dsmEnabled
        ? `${ViewerConstants.COLLECTION_MODES.OBLIQUE.name}_${event.tool}${GoogleAnalyticsConstantsEvents.MEASUREMENT_COMPLETED}`
        : `${ViewerConstants.COLLECTION_MODES.VERTICAL.name}_${event.tool}${GoogleAnalyticsConstantsEvents.MEASUREMENT_COMPLETED}`;
    }
    this.googleAnalyticsService.setGoogleAnalyticsEvent(name, "measure", true);
  }

  /**
   * Event launched when the map is dragged to pan
   * @deprecated do we need this event?
   */
  onDragPanEnd(): void {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.PAN_DRAG_MAP,
      GoogleAnalyticsConstantsCategory.MAP,
      true,
    );
  }

  onAutoDisasterContrast(): void {}

  onContextMenuCenterMapEnd(evt: any): void {
    this.setCenterCoordinates(evt.coordinates);
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.RIGHT_CLICK_CENTER_MAP_HERE,
      GoogleAnalyticsConstantsCategory.CONTEXT_MENU,
      true,
    );
  }

  onContextMenuCopyCoordinatesEnd(): void {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.RIGHT_CLICK_COPY_COORDINATES,
      GoogleAnalyticsConstantsCategory.CONTEXT_MENU,
      true,
    );
  }

  onContextMenuCopyZoomIn(): void {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.RIGHT_CLICK_ZOOM_IN,
      GoogleAnalyticsConstantsCategory.CONTEXT_MENU,
      true,
    );
  }

  onContextMenuCopyZoomOut(): void {
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.RIGHT_CLICK_ZOOM_OUT,
      GoogleAnalyticsConstantsCategory.CONTEXT_MENU,
      true,
    );
  }

  onContextMenuExportFeature(event): void {
    this.exportFeatureSubject.next(event.feature);
  }

  /**
     * Event launched when the map finish loading
     the first time
     @deprecated, initialized on collection worker starts
     */
  // firstLoadFinished(): void {
  //   this.mapService.setMapInitialized(true);
  //   this.mapService.populateMapStatus();
  //   this.updateLayerStatus();
  // }

  /**
   * Event launched after receive projects for collection
   * @param event
   */
  onUpdateLayerProjects(event: any): void {
    const { layer } = event;
    if (layer) {
      const collection = this.getCollections().find((item) => item.layer === layer.layer);
      if (collection) {
        collection.coverageGeom = layer.coverageGeom;
        collection.bboxGeom = layer.bboxGeom;
        collection.bbox = layer.bbox;
        collection.aois = this.fetchCollectionProjectData(layer);
      }
    }
  }

  /**
   * Event launch after all collection aois and coverages are populated from backend
   * We should sync data with state param
   * We should apply mask if are applicable
   */
  onCollectionsPopulated() {
    if (!this.getCoreMap()) {
      return;
    }

    this.hideLoadingComponent();

    // _______ CREATE PROJECT STATUS WITH IMAGES __________ //

    // console.log("On collection populate");
  }

  /**
   * Launch after each movement with status projects
   * Center, extent, zoom
   * List of enable projects
   * List of images for this point
   * We have to calculate data tto use in the UI, images, projects  etc
   * @param e
   */
  onUpdateCollectionStatus(e: any): void {
    if (!this.getCoreMap()) {
      return;
    }

    if (e.images) {
      //Get collections projects
      let projectNames = e.collections.map((c) => c.properties.collection).filter((c) => c !== undefined);

      const projects = this.populateCollectionProjects(e.images, projectNames);

      const mapStatus = this.mapService.updateMapStatus(
        GeoService.pointFromArray(e.status.center),
        GeoService.bboxFromPoints(e.status.extent),
        e.status.zoom,
      );

      this.updateProjectStatus(projects, e.visibleCollection?.properties.collection, mapStatus);
      this.updateTimelineFilters();
      this.updateCenterImageDate();

      this.updateLocationInfo();
      this.updateUrlParameters();
    }
    if (!this.collectionsPopulated) {
      //just first time
      this.mapService.setMapInitialized(true);
      this.collectionsPopulated = true;
      // we have all the projects and status
      this.projectsPopulatedSubject.next(true);
      this.updateAfterMoveEnd();

      const filter = this.getTimelineFilters();
      if (filter) {
        const projects = this.filterProjectsByFilter(filter);
        this.applyTimelineFilter(filter, projects?.visible, projects?.hidden);
      }

      const state = this.urlStateService.getStateParam(this.mapService.getMapId());
      if (state) {
        this.handleState(state);
      }
      this.windowMessageService.send(e);
    }
  }

  updateTimelineFilters() {
    //This method only works with multilayers filters
    if (!this.getProjectStatus()?.multiLayerEnabled) {
      return;
    }

    //Reset the filterCounts
    FilterProjectService.resetFiltersCount(this.getTimelineFilters());

    //Update the collection visibility
    this.getProjectStatus().enabledAOIs.map((aoi) =>
      aoi.setVisible(FilterProjectService.filterProject(aoi, this.getTimelineFilters())),
    );

    //Update the filter visibile value with enabled aois
    FilterProjectService.processAfterFilter(this.getProjectStatus().enabledAOIs, this.getTimelineFilters());
  }

  //
  // /**
  //  * Flattern all projectNames returned on the event
  //  * @param e
  //  */
  // projectsFromEvent(e: any) {
  //   //Flattern all projectNames returned on the event
  //   let projectsNames = [];
  //   if (e?.collections?.length > 0) {
  //     projectsNames = e.collections.reduce((acc, c) => {
  //       return acc.concat(c.projects.map((p) => p.name));
  //     }, []);
  //   }
  //   return projectsNames;
  // }

  /**
   * Handle event when image discrete its loaded
   * @param evt image metadata
   */
  loadDiscreteFinished(evt: any): void {
    const { imageMetadata } = evt;
    this.setImageDiscrete(imageMetadata);
    this.updateUrlParameters();
    this.setCenterCoordinates(this.getLocation());
    this.mapService.setDiscreteEnabled(true);
    this.updateCenterImageDate();
  }

  /**
   * Hide layers before intialize
   * @param initialized
   * @deprecated
   */
  handleCollectionVisibilityBeforeInitialize(initialized) {
    const collections = this.getCoreMap().getCollectionLayers();
    collections.forEach((collection) => {
      collection.setVisible(initialized);
    });
  }

  updateDSMStatus(event: any): void {
    this.ngZone.run(() => {
      this.dsmStatusSubject.next({
        enabled: event.enabled,
        status: event.status,
        inside: event.inside,
        coverage: event.coverage,
        available: event.available,
      });
    });
  }

  onSwipeMoveEnd(event: any): void {
    const mapStatus = this.getCoreMap().getMapStatus(ViewerConstants.PROJECTIONS.WebMercator);
    if (mapStatus && mapStatus.center && !(Number.isNaN(mapStatus.center[0]) || Number.isNaN(mapStatus.center[1]))) {
      const extent = GeoService.bboxFromPoints(mapStatus.extent);
      const center = GeoService.pointFromArray(mapStatus.center);

      this.mapService.updateMapStatus(center, extent, mapStatus.zoom);
    }
    // this.updateProjectStatus(this.getCollections());
    this.getProjectStatus().centerAOI =
      event.barPosition >= 0.5
        ? this.getProjectStatus().mapStatus.swipeStatus.left
        : this.getProjectStatus().mapStatus.swipeStatus.right;
    this.updateLocationInfo();
    this.updateCenterImageDate();
  }

  onRelativeElevation(event: any): void {
    this.relativeElevationStatusSubject.next(event);
  }

  /**
     * Event launched when the map start loading
     the first time
     */
  firstLoadStart(event: any): void {
    this.firstLoadSubject.next(true);
    this.windowMessageService.send(event);
  }

  // endregion

  // region Measures/measurements
  updateMeasurementSystem(systemId: number, silently: boolean = false): void {
    this.measurementSystemIdSubject.next(systemId);
    if (!silently) {
      this.checkMixedMeasurementSystems();
      this.saveUserConfiguration(this.getLayersToSaveConfiguration());
    }
  }

  updatePropertyInfoUnit(unit: number, silently: boolean = false): void {
    this.propertyInfoUnitSubject.next(unit);
    if (!silently) {
      this.checkMixedMeasurementSystems();
      this.saveUserConfiguration(this.getLayersToSaveConfiguration());
    }
  }

  getPropertyInfoUnit(): number {
    return this.propertyInfoUnitSubject.getValue();
  }

  updateMeasurementLengthUnit(unit: string, silently: boolean = false): void {
    this.getMeasurementTools().forEach((tool) => tool.internalTool.setLengthUnits(unit));
    this.measurementLengthUnitSubject.next(unit);
    if (!silently) {
      this.checkMixedMeasurementSystems();
      this.saveUserConfiguration(this.getLayersToSaveConfiguration());
    }
  }

  updateMeasurementAreaUnit(unit: string, silently: boolean = false): void {
    this.getMeasurementTools().forEach((tool) => tool.internalTool.setAreaUnits(unit));
    this.measurementAreaUnitSubject.next(unit);
    if (!silently) {
      this.checkMixedMeasurementSystems();
      this.saveUserConfiguration(this.getLayersToSaveConfiguration());
    }
  }

  updateMeasurementSlopeUnit(unit: string, silently: boolean = false): void {
    this.getMeasurementTools().forEach((tool) => tool.internalTool.setSlopeUnits(unit));
    this.measurementSlopeUnitSubject.next(unit);
    if (!silently) {
      this.checkMixedMeasurementSystems();
      this.saveUserConfiguration(this.getLayersToSaveConfiguration());
    }
  }

  private applyingGlobalSystem = false;

  setGlobalMeasurementSystem(systemId: number) {
    this.viewerConfigurationService.setGlobalMeasurementSystem(systemId);
  }

  private applyMeasurementSystemInternally(systemId: number) {
    if (
      systemId === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.METRIC ||
      systemId === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL
    ) {
      this.applyingGlobalSystem = true;
      this.measurementSystemIdSubject.next(systemId);
      this.propertyInfoUnitSubject.next(systemId);

      const system = ViewerConstants.MEASUREMENT_SYSTEMS.SYSTEMS.find((s) => s.ID === systemId);
      if (system) {
        this.updateMeasurementLengthUnit(system.UNITS.LENGTH_MIDDLE, true);
        const areaUnit =
          systemId === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.METRIC
            ? ViewerConstants.AREA_METRIC_UNITS[0]
            : ViewerConstants.AREA_IMPERIAL_UNITS[0];
        this.updateMeasurementAreaUnit(areaUnit, true);
      }
      this.applyingGlobalSystem = false;
      this.checkMixedMeasurementSystems();
      this.saveUserConfiguration(this.getLayersToSaveConfiguration());
    }
  }

  getGlobalMeasurementSystem(): number {
    return this.viewerConfigurationService.getGlobalMeasurementSystem();
  }

  private isImperialUnit(unit: string): boolean {
    return (
      ViewerConstants.IMPERIAL_UNITS.includes(unit) ||
      ViewerConstants.LENGTH_IMPERIAL_UNITS.includes(unit) ||
      ViewerConstants.AREA_IMPERIAL_UNITS.includes(unit)
    );
  }

  private isMetricUnit(unit: string): boolean {
    return (
      ViewerConstants.METRIC_UNITS.includes(unit) ||
      ViewerConstants.LENGTH_METRIC_UNITS.includes(unit) ||
      ViewerConstants.AREA_METRIC_UNITS.includes(unit)
    );
  }

  private getUnitSystemId(unit: string): number | null {
    if (this.isMetricUnit(unit)) {
      return ViewerConstants.MEASUREMENT_SYSTEMS.IDS.METRIC;
    }
    if (this.isImperialUnit(unit)) {
      return ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL;
    }
    return null;
  }

  checkMixedMeasurementSystems() {
    if (this.applyingGlobalSystem) {
      return;
    }

    const currentGlobal = this.getGlobalMeasurementSystem();

    const detectedSystems = new Set<number>();

    const lengthUnit = this.measurementLengthUnitSubject.getValue();
    if (lengthUnit) {
      const sys = this.getUnitSystemId(lengthUnit);
      if (sys !== null) {
        detectedSystems.add(sys);
      }
    }

    const areaUnit = this.measurementAreaUnitSubject.getValue();
    if (areaUnit) {
      const sys = this.getUnitSystemId(areaUnit);
      if (sys !== null) {
        detectedSystems.add(sys);
      }
    }

    const propertyUnit = this.propertyInfoUnitSubject.getValue();
    if (propertyUnit !== null && propertyUnit !== undefined) {
      detectedSystems.add(propertyUnit);
    }

    const scaleBarSystem = this.measurementSystemIdSubject.getValue();
    if (
      scaleBarSystem === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.METRIC ||
      scaleBarSystem === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL
    ) {
      detectedSystems.add(scaleBarSystem);
    }

    if (detectedSystems.size > 1) {
      this.viewerConfigurationService.setGlobalMeasurementSystem(ViewerConstants.MEASUREMENT_SYSTEMS.IDS.MIXED);
    } else if (detectedSystems.size === 1) {
      const detectedSystem = Array.from(detectedSystems)[0];
      if (currentGlobal === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.MIXED) {
        // All units are back to the same system — restore from MIXED
        this.viewerConfigurationService.setGlobalMeasurementSystem(detectedSystem);
      } else if (
        (currentGlobal === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.METRIC ||
          currentGlobal === ViewerConstants.MEASUREMENT_SYSTEMS.IDS.IMPERIAL) &&
        detectedSystem !== currentGlobal
      ) {
        this.viewerConfigurationService.setGlobalMeasurementSystem(ViewerConstants.MEASUREMENT_SYSTEMS.IDS.MIXED);
      }
    }
  }

  getMeasurementConversion(type: string, originSystemId: number, destSystemId?: number): string {
    // eslint-disable-next-line no-param-reassign
    destSystemId = destSystemId || this.getMeasurementSystemId();
    const originSystem = ViewerConstants.MEASUREMENT_SYSTEMS.SYSTEMS.find((system) => system.ID === originSystemId);
    const destSystem = ViewerConstants.MEASUREMENT_SYSTEMS.SYSTEMS.find((system) => system.ID === destSystemId);
    return `${originSystem.UNITS[type]}-${destSystem.UNITS[type]}`;
  }

  /**
   * Enable measurements selection
   * We deactivate active tools
   */
  enableMeasuresSelection() {
    this.deactivateTools();
    this.mapService.enableMeasuresSelection();
  }

  selectMeasure(id: string) {
    this.mapService.selectMeasure(id);
  }

  /**
   * Disable measurements selection
   */
  disableMeasuresSelection() {
    this.mapService.disableMeasuresSelection();
  }

  enableDragMeasurement() {
    this.mapService.enableDragMeasurement();
  }

  disableDragMeasurement() {
    this.mapService.disableDragMeasurement();
  }

  enableEditMeasurement() {
    this.mapService.enableEditMeasurement();
  }

  disableEditMeasurement() {
    this.mapService.disableEditMeasurement();
  }

  // endregion

  getColorRgbA(): string {
    return this.mapService.getColorRgbA();
  }

  setColor(colorHexCode: string, features: any[]) {
    this.mapService.setColor(colorHexCode, features);
  }

  setColorRgbA(colorRgba: string, features: any[]) {
    this.mapService.setColorRgbA(colorRgba, features);
  }

  showLoadingComponent(): void {
    this.loadingService.showLoadingComponent("tiles");
  }

  hideLoadingComponent(): void {
    this.loadingService.hideLoadingComponent("tiles");
  }

  // region Layers Management

  getLayerById(layerId: string): Viewerlayer {
    const layers = [...(this.collectionLayersRaw ?? []), ...(this.collectionProjects ?? [])];
    return layers.find((layer) => layer.id === layerId);
  }

  addFeatureCollection(evt: any): void {
    const layer = this.layerService.createLayerFromLocalCollection(evt.collection);
    AiLayerStyles.attachStyleDefinition(layer, evt.collection);

    this.geoJSONDatabaseService.saveLayer(layer).subscribe(() => {
      this.addUserLayer(layer);
      this.updateUserOrder(this.getUserLayers());
    });
  }

  /**
   * Adds a GeoTIFF as a user layer, referenced by URL, and persists it to IndexedDB.
   * @param url - URL to the GeoTIFF file.
   * @param name - Optional display name for the layer.
   */
  async addGeoTiffLayer(url: string, name?: string): Promise<void> {
    // Fetches the file client-side to: (1) work around a tie-point georeferencing bug seen in
    // some backend DSM/DTM extracts, which otherwise misplaces the whole layer, and (2) compute
    // the real band value range — ol/source/GeoTIFF only auto-normalizes against embedded GDAL
    // statistics tags, and without real min/max the WebGL layer stretches against the full
    // data-type range (e.g. all of float32) and renders solid black.
    const prepared = await this.geoTiffRendererService.prepareForMap(url);
    if (!prepared) {
      console.error("BaseViewerService: failed to prepare GeoTIFF for map", url);
      return;
    }
    const layer = this.layerService.createGeoTiffLayerDef(prepared.blob, name, prepared);

    this.geoJSONDatabaseService.saveLayer(layer).subscribe(() => {
      this.addUserLayer(layer);
      this.updateUserOrder(this.getUserLayers());
      // bbox isn't known until the file's own georeferencing is read off the map layer —
      // resolve it asynchronously and patch the already-persisted layer once available,
      // so "highlight on map" (fit-to-layer) works the same way it does for GeoJSON layers.
      void this.layerService.resolveGeoTiffBbox(layer.id, this.getCoreMap()).then((bbox) => {
        if (!bbox) return;
        layer.bbox = bbox;
        this.updateUserLayers(this.userLayersRaw);
        this.geoJSONDatabaseService.updateLayer(layer).subscribe();
      });
    });
  }

  /**
   * Adds multiple feature collections as user layers in a single batch operation.
   * @param evts Array of objects with { collection, styleFunctionName? }
   */
  addFeatureCollections(evts: { collection: any; styleFunctionName?: string; name?: string }[]): void {
    const layers = evts.map((evt) => {
      const layer = this.layerService.createLayerFromLocalCollection(evt.collection);
      if (evt.styleFunctionName) {
        layer.styleFunctionName = evt.styleFunctionName;
      }
      if (evt.name) {
        layer.name = evt.name;
      }
      AiLayerStyles.attachStyleDefinition(layer, evt.collection);
      return layer;
    });
    // styleFunctionName is a string on the layer here — serializable for IndexedDB.
    // applyStyleFunctions is called after save so live function refs don't corrupt serialization.
    this.geoJSONDatabaseService.saveLayers(layers).subscribe((success) => {
      if (success) {
        AiLayerStyles.applyStyleFunctions(layers);
        this.userLayersRaw = this.userLayersRaw.concat(layers);
        this.updateUserLayers(this.userLayersRaw);
        this.layerService.addLayersToMap(layers, this.getCoreMap());
      } else {
        this.errorAddFeatureCollection({ errorMessage: "Cannot add user layers" });
      }
    });
  }

  errorAddFeatureCollection(evt: any): void {
    this.viewerErrorCommunicatorService.setViewerError(evt.errorMessage);
  }

  /**
   * Creates a named group layer from multiple prepared GeoJSON FeatureCollections,
   * persists it to IndexedDB, and adds it to the map.
   * Children are stored embedded in the group record (not as separate IndexedDB entries).
   * @param name - Display name for the group layer.
   * @param layers - Array of { collection, label?, visible?, layer_group? }. label overrides the child layer
   *   name (defaults to collection filename). visible controls initial child visibility (defaults to true).
   *   layer_group marks pipeline layer types ("properties", "damage", "vlm") so clicking features opens the relevant panel.
   */
  addGroupLayer(
    name: string,
    layers: { collection: any; label?: string; visible?: boolean; layer_group?: string }[],
  ): void {
    const { userId } = this.viewerConfigurationService.getViewerConfiguration();
    const groupId = crypto.randomUUID();

    const children: Viewerlayer[] = layers.map(({ collection, label, visible, layer_group }, index) => {
      if (layer_group === "properties" || layer_group === "damage" || layer_group === "embeddings") {
        const centroidFeatures: any[] = [];
        collection.features?.forEach((f: any) => {
          let centroidCoords: number[] | null = null;
          try {
            const wgs84 = GeoService.toWgs84(f, false);
            centroidCoords = GeoService.getCenterOfGeometry(wgs84).geometry.coordinates;
          } catch {
            // centroid not available — panel will open without map pin
          }
          // For embeddings, _pipeline_layer_group is already set by preprocessing; preserve existing properties
          f.properties = { ...f.properties, _pipeline_centroid: centroidCoords };
          if (layer_group !== "embeddings") {
            f.properties._pipeline_layer_group = layer_group;
          }
          if (layer_group === "embeddings" && centroidCoords) {
            const centroid3857 = GeoService.toMercator(
              { type: "Feature", geometry: { type: "Point", coordinates: centroidCoords } },
              false,
            );
            centroidFeatures.push({
              type: "Feature",
              id: crypto.randomUUID(),
              geometry: centroid3857.geometry,
              properties: { ...f.properties },
            });
          }
        });
        if (centroidFeatures.length) {
          collection.features = [...(collection.features ?? []), ...centroidFeatures];
        }
      } else if (layer_group === "vlm") {
        collection.features?.forEach((f: any) => {
          f.properties = { ...f.properties, _pipeline_layer_group: "vlm" };
        });
      } else if (layer_group === "geofence") {
        collection.features?.forEach((f: any) => {
          f.properties = { ...f.properties, _pipeline_layer_group: "geofence" };
        });
      } else if (layer_group === "aoi") {
        collection.features?.forEach((f: any) => {
          f.properties = { ...f.properties, _pipeline_layer_group: "aoi" };
          if (f.properties.type === "poi") {
            try {
              const wgs84 = GeoService.toWgs84(f, false);
              const centroid = GeoService.getCenterOfGeometry(wgs84).geometry.coordinates;
              f.properties._pipeline_centroid = centroid;
            } catch {
              // centroid not available — dialog will open without map pin
            }
          }
        });
      }

      const child = this.layerService.createLayerFromLocalCollection(collection);
      AiLayerStyles.attachStyleDefinition(child, collection);
      child.parent_id = groupId;
      child.group_order = index;
      child.childLayer = true;
      child.layerGroup = layer_group;
      if (label !== undefined) child.name = label;
      if (visible !== undefined) child.visible = visible;
      if (layer_group === "properties") {
        child.styleFunctionName = "propertyPipelineStyle";
      } else if (layer_group === "embeddings") {
        child.styleFunctionName = "embeddingsPipelineStyle";
      } else if (layer_group === "geofence") {
        child.styleFunctionName = "geofencePipelineStyle";
      } else if (layer_group === "aoi") {
        child.styleFunctionName = "aoiPipelineStyle";
      } else if (layer_group === "merged") {
        child.styleFunctionName = "mergedPipelineStyle";
      } else {
        const styleColors = layer_group ? AiLayerStyles.PIPELINE_LAYER_STYLES[layer_group] : undefined;
        if (styleColors) {
          child.style = JSON.stringify({
            defaultSetting: {
              colorFill: styleColors.colorFill,
              colorFillOpacity: "0.3",
              colorOutline: styleColors.colorOutline,
              colorOutlineOpacity: "1",
            },
          });
        }
      }
      return child;
    });

    const groupLayer = {
      id: groupId,
      userId,
      name,
      layerType: ViewerConstants.LAYER_TYPE.GROUPLAYER,
      sourceName: ViewerConstants.SOURCE_NAME.GROUP,
      base: false,
      baseLayer: false,
      visible: true,
      owner: true,
      removable: true,
      editable: false,
      sharable: false,
      enabled: true,
      expandable: true,
      childs: children,
      order: ViewerConstants.USER_LAYER_BASE_ZINDEX,
    } as Viewerlayer;

    this.geoJSONDatabaseService.saveLayer(groupLayer).subscribe((success) => {
      if (success) {
        this.addUserLayer(groupLayer);
        this.updateUserOrder(this.getUserLayers());
      } else {
        console.error(`[Pipeline] Failed to save group layer "${name}" to IndexedDB`);
        this.errorAddFeatureCollection({ errorMessage: "Cannot add group layer" });
      }
    });
  }

  addUserLayer(layerToSave) {
    AiLayerStyles.applyStyleFunctions([layerToSave]);
    this.userLayersRaw.push(layerToSave);
    this.updateUserLayers(this.userLayersRaw);
    this.layerService.addLayersToMap([layerToSave], this.getCoreMap());
  }

  updateUserLayerStyle(layer) {
    if (layer.layerType === ViewerConstants.LAYER_TYPE.GROUPLAYER) return;
    this.geoJSONDatabaseService.updateLayer(layer).subscribe(() => {
      this.getCoreMap().updateUserLayerStyles(
        this.getUserLayers().filter((l) => l.layerType !== ViewerConstants.LAYER_TYPE.GROUPLAYER),
      );
    });
  }

  /**
   * Finds a user layer by id, searching both top-level layers and children nested inside a
   * GROUPLAYER at any depth. Used by callers (e.g. the AI apply_layer_style tool) that only have
   * a layer id, not a live Viewerlayer reference.
   */
  findUserLayerById(id: string): Viewerlayer | undefined {
    return LayerUtilsService.flattenWithChildren(this.getUserLayers()).find((layer) => layer.id === id);
  }

  /** Finds the GROUPLAYER record that owns `childId` as one of its `childs`, if any. */
  private findParentGroupLayer(childId: string): Viewerlayer | undefined {
    return this.userLayersRaw.find(
      (l) => l.layerType === ViewerConstants.LAYER_TYPE.GROUPLAYER && l.childs?.some((c) => c.id === childId),
    );
  }

  /**
   * Persists a new `styleDefinition` (flat/rules vector style) for a layer and re-renders it live.
   * Unlike `updateUserLayerStyle`, this also handles layers embedded as a GROUPLAYER child — the
   * child's own record is never a top-level IndexedDB entry, so its parent group must be re-saved.
   * `styleMetadata` is persisted alongside `styleDefinition` (not just shown transiently) so it can
   * be echoed back verbatim later, e.g. by `get_layer_geometry`.
   * Returns an observable of whether the style was successfully applied and persisted — `false`
   * on a malformed style definition or a failed IndexedDB write, never throws.
   */
  updateLayerStyleDefinition(
    layer: Viewerlayer,
    styleDefinition: IVectorStyle | undefined,
    styleMetadata?: IStyleLegendMetadata,
    styleFailureHooks?: { onRejection?: (error: unknown) => void },
  ): Observable<boolean> {
    const resolved = styleDefinition ? convertVectorStyle(styleDefinition) : null;
    if (styleDefinition && !resolved) {
      console.error("BaseViewerService: malformed styleDefinition, not applying", layer.id, styleDefinition);
      return of(false);
    }

    layer.styleDefinition = styleDefinition;
    layer.styleMetadata = styleMetadata;
    if (resolved) layer.styleFunction = resolved;

    const recordToPersist = layer.parent_id ? this.findParentGroupLayer(layer.id) : layer;
    if (!recordToPersist) {
      console.error("BaseViewerService: no persistable record found for layer", layer.id);
      return of(false);
    }

    return this.geoJSONDatabaseService.updateLayer(stripStyleFunctions(recordToPersist)).pipe(
      tap((success) => {
        if (!success) {
          console.error("BaseViewerService: failed to persist styleDefinition for layer", layer.id);
          return;
        }
        this.updateUserLayers(this.userLayersRaw);
        const olLayer = this.mapService.getOverlayManager()?.getLayerById(layer.id);
        if (olLayer && resolved) {
          try {
            (olLayer as any).setStyle(resolved);
          } catch (error) {
            // OL throws synchronously for malformed expressions — must not crash the caller, the style was already persisted.
            console.error(
              "BaseViewerService: OL rejected the resolved style, layer left unstyled",
              layer.id,
              resolved,
              error,
            );
            // S10: the rejection is user-reachable — callers may surface it through the app
            // toast system without changing this observable's documented boolean semantics.
            styleFailureHooks?.onRejection?.(error);
          }
        }
      }),
    );
  }

  /**
   * Applies a resolved style to a live OL layer WITHOUT persisting it. Used for transient
   * UI toggles (e.g. the legend panel's per-rule visibility toggle) — the persisted
   * `styleDefinition` on the Viewerlayer record and in IndexedDB stays intact, so a reload
   * or a full `updateLayerStyleDefinition` call restores the original style.
   */
  applyLayerStyleTransient(layer: Viewerlayer, styleDefinition: IVectorStyle): boolean {
    const resolved = convertVectorStyle(styleDefinition);
    if (!resolved) {
      console.error("BaseViewerService: malformed transient styleDefinition, not applying", layer.id, styleDefinition);
      return false;
    }
    const olLayer = this.mapService.getOverlayManager()?.getLayerById(layer.id);
    if (!olLayer) return false;
    try {
      (olLayer as any).setStyle(resolved);
      return true;
    } catch (error) {
      console.error("BaseViewerService: OL rejected transient style", layer.id, resolved, error);
      return false;
    }
  }

  updateUserLayer(layer) {
    // Force update layer in IndexedDB
    this.geoJSONDatabaseService.updateLayer(layer).toPromise();
  }

  featuresLoaded(event: any): void {
    this.userLayersRaw.forEach((l) => {
      if (l.id === event.layer.id && event.features) {
        l.customOverlayProperties = this.layerService.getCustomOverlayProperties(l, event.features);
        l.source.features = {
          features: null,
        };
        const sourceFeatures = this.getSourceFeatures(event.features, event.layer);
        if (sourceFeatures) {
          l.source.features.features = sourceFeatures;
        } else {
          l.customOverlayProperties = null;
        }
      }
    });
    this.updateUserLayers(this.userLayersRaw);
  }

  getSourceFeatures(features: any, layer: any): any {
    const sourceFeatures = [];
    features.forEach((feature) => {
      const properties = { ...feature.values_ };
      delete properties.geometry;
      const f = {
        geometry: {
          type: typeof feature.getGeometry(),
          coordinates: feature.getGeometry().flatCoordinates,
        },
        headers: layer.customOverlayProperties.layerProperties,
        layerName: layer.name,
        properties,
        type: "Feature",
        id: feature.id,
      };
      sourceFeatures.push(f);
    });
    return sourceFeatures;
  }

  refreshCollectionLayers() {
    this.updateCollectionLayers(this.getCollections());
    this.changeLayers();
  }

  updateCollectionLayers(collections: Viewerlayer[]) {
    this.collectionLayersRaw = collections.sort((l1, l2) => l2.order - l1.order); // first with the order bigger
    this.collectionLayersSubject.next(this.collectionLayersRaw);
  }

  updateCollectionOrder(layers: Viewerlayer[], silently = false): void {
    if (!this.getCoreMap()) {
      return;
    }
    layers.forEach((element, index) => {
      const order = ViewerConstants.COLLECTION_OFFSET - index;
      element.order = order;
      element.aois?.forEach((aoi, i) => {
        aoi.order = order;
        aoi.group_order = i; // relative order inside a group, because the order its the same than the parent
        aoi.aois?.forEach((child, j) => {
          child.order = order;
          child.group_order = j; // relative order inside a group, because the order its the same than the parent
          child.parent_group_order = i;
        });
      });
    });

    this.updateCollectionLayers(layers);

    this.getCoreMap().updateCollectionsOrder(layers);
    if (!silently) {
      this.changeLayers();
    }
  }

  addBaseLayers(layers: Viewerlayer[]): void {
    this.updateBaseLayers(layers, true);
  }

  addOverlayLayers(layers: Viewerlayer[]): void {
    this.updateOverlayOrder(layers, true);
    this.updateOverlayLayers(layers, true);
  }

  addUserLayers(layers: Viewerlayer[]): void {
    AiLayerStyles.applyStyleFunctions(layers);
    this.layerService.addLayersToMap(layers, this.getCoreMap());
    this.updateUserOrder(layers);
    this.updateUserLayers(layers);
  }

  removeUserLayers(layerIds: string[]) {
    this.geoJSONDatabaseService.deleteLayers(layerIds).subscribe((deleted) => {
      if (deleted) {
        layerIds.forEach((l) => this.layerService.removeLayerById(l, this.getCoreMap()));
        const layers = this.userLayersRaw.filter((layer) => !layerIds.find((l) => layer.id === l));
        this.updateUserLayers(layers);
      } else {
        this.viewerErrorCommunicatorService.setViewerError("Cannot delete this users layer");
      }
    });
  }

  removeUserLayer(layerId: string) {
    this.geoJSONDatabaseService.deleteLayer(layerId).subscribe((deleted) => {
      if (deleted) {
        this.layerService.removeLayerById(layerId, this.getCoreMap());
        const layers = this.userLayersRaw.filter((layer) => layer.id !== layerId);
        this.updateUserLayers(layers);
      } else {
        this.viewerErrorCommunicatorService.setViewerError("Cannot delete this user layer");
      }
    });
  }

  /**
   * Changes the visibility of a single child layer inside a group.
   * Updates the in-memory child state, the map, and persists the parent to IndexedDB.
   * @param childId - ID of the child layer to toggle.
   * @param visibility - New visibility value.
   */
  changeGroupChildVisibility(childId: string, visibility: boolean): void {
    const parent = this.userLayersRaw.find(
      (l) => l.layerType === ViewerConstants.LAYER_TYPE.GROUPLAYER && l.childs?.some((c) => c.id === childId),
    );
    if (!parent) return;
    const child = parent.childs!.find((c) => c.id === childId);
    if (child) child.visible = visibility;
    this.mapService.changeVisibility(childId, visibility);
    this.geoJSONDatabaseService.updateLayer(parent).toPromise();
  }

  /**
   * Removes a single child layer from its parent group layer.
   * If the group becomes empty after removal, the entire group is deleted.
   * @param childId - ID of the child layer to remove.
   */
  removeGroupChild(childId: string): void {
    const parent = this.userLayersRaw.find(
      (l) => l.layerType === ViewerConstants.LAYER_TYPE.GROUPLAYER && l.childs?.some((c) => c.id === childId),
    );
    if (!parent) return;
    this.layerService.removeLayerById(childId, this.getCoreMap());
    parent.childs = parent.childs!.filter((c) => c.id !== childId);
    if (!parent.childs.length) {
      this.removeUserLayer(parent.id);
    } else {
      this.geoJSONDatabaseService.updateLayer(parent).toPromise();
      this.updateUserLayers(this.userLayersRaw);
    }
  }

  /**
   * Upserts a stable-identity group layer: returns the existing `LAYER_TYPE.GROUPLAYER` whose
   * `id` matches `descriptor.key`, or creates and registers a clean one when absent. Unlike
   * `addGroupLayer` (legacy pipeline rules), the created group carries NO `_pipeline_*`
   * properties, NO centroid synthesis and NO style-by-`layer_group` mapping, and it is not put
   * on the map by itself — `addEvidenceLayer` appends children and renders those. `id` doubles
   * as the IndexedDB `user-layers` keyPath (`keyPath: "id", autoIncrement: false`), so the
   * deterministic string key persists as the record identity across reloads (legacy groups keep
   * their random uuid ids; keys like "plan:<plan_ref>" cannot collide with them).
   * @param descriptor - Stable group key plus optional display name (deterministic fallback
   *   derived from the key when absent).
   */
  ensureLayerGroup(descriptor: { key: string; name?: string; description?: string }): Viewerlayer {
    const existing = this.userLayersRaw.find(
      (layer) => layer.layerType === ViewerConstants.LAYER_TYPE.GROUPLAYER && layer.id === descriptor.key,
    );
    if (existing) return existing;

    const { userId } = this.viewerConfigurationService.getViewerConfiguration();
    const groupLayer = {
      id: descriptor.key,
      userId,
      name: descriptor.name ?? fallbackGroupDisplayName(descriptor.key),
      // Full human-readable context for tooltip consumers (display name may be truncated).
      ...(descriptor.description ? { description: descriptor.description } : {}),
      layerType: ViewerConstants.LAYER_TYPE.GROUPLAYER,
      sourceName: ViewerConstants.SOURCE_NAME.GROUP,
      base: false,
      baseLayer: false,
      visible: true,
      owner: true,
      removable: true,
      editable: false,
      sharable: false,
      enabled: true,
      expandable: true,
      childs: [],
      order: ViewerConstants.USER_LAYER_BASE_ZINDEX,
    } as Viewerlayer;
    this.userLayersRaw = this.userLayersRaw.concat([groupLayer]);
    this.updateUserLayers(this.userLayersRaw);
    return groupLayer;
  }

  /**
   * Adds a prepared GeoJSON FeatureCollection as an evidence layer — the generic map-side
   * primitive behind the chat-side `add_evidence_layer` event. With a group descriptor the
   * child joins the stable group (upserted via `ensureLayerGroup`) with the same binding fields
   * `addGroupLayer` children carry (`parent_id`/`group_order`/`childLayer`) but NO pipeline
   * mutations, the group record is re-persisted (children are embedded in ONE IndexedDB record,
   * precedent `removeGroupChild`), and the appended child is rendered on the map the way other
   * user layers are. Without a group the child is added standalone, mirroring the
   * single-collection path of `addFeatureCollections`. An embedded `.style` definition wins over
   * `styleFunctionName` — existing `applyStyleFunctions` semantics.
   * @param request - { collection, name?, styleFunctionName, group? } with the collection already
   *   prepared by FileReaderService.prepareGeoJson().
   */
  addEvidenceLayer(request: EvidenceLayerAddRequest): void {
    const child = this.layerService.createLayerFromLocalCollection(request.collection);
    child.name = request.name ?? request.collection.properties.name;
    child.styleFunctionName = request.styleFunctionName;
    AiLayerStyles.attachStyleDefinition(child, request.collection);
    // Advanced-editor compatibility: when the collection carries no Style Agent definition,
    // attach the built-in declarative definition for the routed style so the advanced style
    // editor can read and edit it (applyStyleFunctions prefers styleDefinition over the
    // named function, which remains the serializable fallback identity).
    if (!child.styleDefinition) {
      child.styleDefinition = AiLayerStyles.EVIDENCE_STYLE_DEFINITIONS[request.styleFunctionName];
    }

    if (request.group) {
      const group = this.ensureLayerGroup(request.group);
      child.parent_id = group.id;
      child.group_order = group.childs?.length ?? 0;
      child.childLayer = true;
      group.childs = [...(group.childs ?? []), child];
      // Children are stored embedded in the group record (not as separate IndexedDB entries) —
      // persist the group; stripStyleFunctions keeps live OL functions out of IndexedDB.
      // RxJS-native subscription (NOT .toPromise()): updateLayer emits but never completes, so
      // a promise-based consumer would hang forever and never render the child in-session.
      this.geoJSONDatabaseService.updateLayer(stripStyleFunctions(group)).subscribe((persisted) => {
        if (!persisted) {
          console.error(`BaseViewerService: failed to persist group "${group.id}" for evidence layer "${child.id}"`);
          return;
        }
        // applyStyleFunctions runs after persistence so live function refs don't corrupt serialization.
        AiLayerStyles.applyStyleFunctions([child]);
        this.updateUserLayers(this.userLayersRaw);
        this.layerService.addLayersToMap([child], this.getCoreMap());
      });
      return;
    }

    this.geoJSONDatabaseService.saveLayers([child]).subscribe((success) => {
      if (success) {
        AiLayerStyles.applyStyleFunctions([child]);
        this.userLayersRaw = this.userLayersRaw.concat([child]);
        this.updateUserLayers(this.userLayersRaw);
        this.layerService.addLayersToMap([child], this.getCoreMap());
      } else {
        this.errorAddFeatureCollection({ errorMessage: "Cannot add user layers" });
      }
    });
  }

  /**
   * Replaces the data of an existing user layer IN PLACE (map-layer-block-ui S5): new prepared
   * FeatureCollection, optional display rename, optional style. The live layer is resolved by id
   * across the whole user layer tree (group children included, `findUserLayerById` precedent) and
   * the record that owns it is rewritten: standalone layers are their own IndexedDB entry, group
   * children are embedded in their PARENT group record which is re-saved instead.
   *
   * The collection must already be prepared by FileReaderService.prepareGeoJson (Mercator
   * geometries, uuid feature ids, `.properties` metadata) — the same contract as
   * `addEvidenceLayer`. The layer keeps its id: this is an UPDATE, never a re-add.
   *
   * Layer not found, not a replaceable vector layer, or without a persistable record → no-op:
   * warns/errors and emits `false`. The caller (map-layer card) falls back to ADD; this primitive
   * never auto-adds.
   *
   * Returns an observable of whether the replace + persist succeeded — never throws.
   */
  /**
   * S10: user-reachable evidence-layer failures surface through the app toast system — the
   * existing console logging stays alongside. Success paths stay silent.
   */
  private notifyEvidenceLayerFailure(message: string): void {
    this.toastService.setNotification({ message, type: TOAST_STATUS.ERROR });
  }

  updateEvidenceLayer(request: EvidenceLayerUpdateRequest): Observable<boolean> {
    const layer = this.findUserLayerById(request.layer_id);
    if (!layer) {
      console.warn(`BaseViewerService: updateEvidenceLayer — layer "${request.layer_id}" not found; caller may fall back to add`);
      this.notifyEvidenceLayerFailure("The layer to update was not found on the map");
      return of(false);
    }
    // Only vector user layers carry a replaceable FeatureCollection source. A group id resolves
    // in the tree too, but mutating a group (or a GeoTIFF) record here would corrupt persisted state.
    if (layer.layerType !== ViewerConstants.SOURCE_NAME.VECTOR) {
      console.warn(
        `BaseViewerService: updateEvidenceLayer — layer "${request.layer_id}" is not a vector layer (${layer.layerType}); refusing to replace its data`,
      );
      this.notifyEvidenceLayerFailure("Only vector layers can have their data replaced");
      return of(false);
    }
    if (!request.collection?.properties) {
      console.warn("BaseViewerService: updateEvidenceLayer — collection is not a prepared FeatureCollection; refusing to replace the layer data");
      this.notifyEvidenceLayerFailure("The received layer data could not be applied");
      return of(false);
    }

    // Replace the data following the createLayerFromLocalCollection conventions: same
    // null-geometry defense, fresh vector source built from the prepared collection, bbox from
    // the collection metadata.
    let collection = request.collection;
    if (Array.isArray(collection.features)) {
      const usable = collection.features.filter((feature: any) => feature && feature.geometry);
      if (usable.length !== collection.features.length) {
        console.warn(`updateEvidenceLayer: dropped ${collection.features.length - usable.length} null-geometry feature(s)`);
        collection = { ...collection, features: usable };
      }
    }
    const source = new LayerSource();
    source.name = ViewerConstants.SOURCE_NAME.VECTOR;
    source.epsg = collection.properties.epsg;
    source.features = collection;
    layer.source = source;
    layer.bbox = collection.properties.bbox;
    if (request.name) {
      layer.name = request.name;
    }

    // Standalone layers are their own IndexedDB entry; group children are embedded in the parent
    // group record — persist exactly the record that owns the data.
    const recordToPersist = layer.parent_id ? this.findParentGroupLayer(layer.id) : layer;
    if (!recordToPersist) {
      console.error("BaseViewerService: no persistable record found for layer", layer.id);
      this.notifyEvidenceLayerFailure("The layer could not be updated — saving failed");
      return of(false);
    }

    // One-shot IndexedDB write consumed with RxJS-native subscription semantics (NOT .toPromise()):
    // updateLayer emits but may never complete (defect round 4/4b), so the re-render must react to
    // the emission. style functions never reach IndexedDB (stripStyleFunctions).
    return this.geoJSONDatabaseService.updateLayer(stripStyleFunctions(recordToPersist)).pipe(
      tap((persisted) => {
        if (!persisted) {
          console.error("BaseViewerService: failed to persist updated features for layer", layer.id);
          this.notifyEvidenceLayerFailure("The layer could not be updated — saving failed");
          return;
        }
        this.updateUserLayers(this.userLayersRaw);
        const olLayer = this.mapService.getOverlayManager()?.getLayerById(layer.id);
        if (olLayer) {
          try {
            // Same feature construction as map add time (LayerFactory): the prepared collection is
            // read as-is (its coordinates are already in the map projection) and each OL feature
            // keeps a cloned originalGeometry so oblique reprojection keeps working.
            const olFeatures = new GeoJSON().readFeatures(collection);
            olFeatures.forEach((feature: any) => {
              feature.originalGeometry = feature.getGeometry().clone();
            });
            olLayer.clear();
            olLayer.addFeatures(olFeatures);
          } catch (error) {
            // OL can reject malformed features synchronously — must not crash the caller; the
            // updated data is already persisted.
            console.error("BaseViewerService: failed to refresh OL features for layer", layer.id, error);
            this.notifyEvidenceLayerFailure("The layer was updated but the map could not refresh its contents");
          }
        }
        if (request.style) {
          // The style path reuses the existing primitive: it persists the definition, re-renders
          // the style live and safely handles OL rejections. One-shot write — consumed with
          // .subscribe(); BOTH failure modes notify (S10): a refused definition (the boolean
          // false) and an OL-level rejection (the onRejection hook).
          this.updateLayerStyleDefinition(layer, request.style, request.style_metadata, {
            onRejection: () =>
              this.notifyEvidenceLayerFailure("The layer was updated but its style could not be applied"),
          }).subscribe((applied) => {
            if (!applied) {
              this.notifyEvidenceLayerFailure("The layer was updated but its style could not be applied");
            }
          });
        }
      }),
    );
  }

  // endregion

  // region Collection / projects
  pushAOIToTop(project: IProjectAOI) {
    this.mapService.pushProject(project);
    // trigger update collection event and this refresh the parameters
    // this.changeLayers();
  }

  changeProject(aoi: IProjectAOI) {
    if (!aoi.isVisible()) this.setSelectedAoi(aoi);
    if (this.is3DMode()) {
      this.show3DProject(aoi);
    } else {
      this.show2DProject(aoi);
    }
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.TIMELINE_CHANGE_AOI,
      GoogleAnalyticsConstantsCategory.TIMELINE,
      true,
    );
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      aoi.isGraySky()
        ? GoogleAnalyticsConstantsEvents.TIMELINE_AOI_GRAY_SKY
        : GoogleAnalyticsConstantsEvents.TIMELINE_AOI_BLUE_SKY,
      GoogleAnalyticsConstantsCategory.TIMELINE,
      true,
    );
  }

  show2DProject(aoi: IProjectAOI) {
    this.showProject(aoi);
    this.collectionMode = ViewerConstants.COLLECTION_MODES.VERTICAL;
    this.mapService.setDiscreteEnabled(false);
  }

  is3DMode(): boolean {
    return this.collectionMode.name === ViewerConstants.COLLECTION_MODES.OBLIQUE.name;
  }

  show3DProject(
    aoi: IProjectAOI,
    img?: IMetadataImage,
    point: [number, number] = null,
    orientation: string = null,
    epsg: string = null,
  ) {
    this.showProject(aoi);

    const layerId = aoi.isAOI() ? aoi.getCollectionId() : aoi.getLayerId();
    if (img) {
      this.getCoreMap().changeToObliqueImage(layerId, img.image_id, img.orientation, img.projectname, img.layername);
      this.orientationSelected = img.orientation;
    } else {
      const aoiName = aoi.getName();
      this.orientationSelected = orientation ?? this.orientationSelected;
      this.mapService
        .getCoreMap()
        .changeCollectionOblique(layerId, null, this.orientationSelected, null, aoiName, point, epsg);
    }
    this.mapService.setDiscreteOrientation(this.orientationSelected);
    this.getCoreMap().changePropertyFootprintLayerVisibility(false);
    this.closePointInfoPopups();
    this.mapService.setDiscreteEnabled(true);
  }

  /**
   * Change visibility to true to aoi and his parent
   * Put aoi and parent in top
   * @param aoi
   * @param pushToTop
   * @param forceUpdateProjects
   */
  showProject(aoi: IProjectAOI, pushToTop: boolean = true, forceUpdateProjects: boolean = true) {
    if (aoi?.isAOI()) {
      if (!aoi.isVisible()) {
        this.changeAOIVisibility(aoi.getLayerId(), true, false, forceUpdateProjects);
      }
      if (aoi.isChildAOI() && !aoi.isParentVisible()) {
        this.changeAOIVisibility(aoi.getParentId(), true, false, forceUpdateProjects);
      }
      if (!aoi.isCollectionVisible()) {
        this.changeCollectionVisibility(aoi.getCollectionId(), true, false, forceUpdateProjects);
      }
      if (pushToTop) this.pushAOIToTop(aoi);
    }

    if (aoi && !aoi.isAOI()) {
      if (!aoi.isVisible()) {
        this.changeCollectionVisibility(aoi.getLayerId(), true);
      }
    }
  }

  /**
   * Method for switch collection on the new multilayer
   * @param aoi
   */
  showCollection(aoi: IProjectAOI) {
    if (aoi) {
      const overlayManager = this.mapService.getOverlayManager();
      overlayManager.showCollection(aoi.name);
    }
  }

  // endregion
  toggleInfrared(): boolean {
    const infraredStatus = this.getInfraredStatus();
    if (!infraredStatus) {
      this.mapService.getOverlayManager().enableInfraredMode();
    } else {
      this.mapService.getOverlayManager().disableInfraredMode();
    }
    this.infraredStatusSubject.next(!infraredStatus);
    this.updateUrlParameters();
    return this.getInfraredStatus();
  }

  changeInfraredStatus(collectionId: string, status: boolean) {
    this.updateUrlParameters(); // to update nir status in state parameters
    const coreMap = this.getCoreMap();
    if (!coreMap || !collectionId) {
      return;
    }
    coreMap.changeInfraredSource(collectionId, status);
  }

  getCoreMap(): any {
    return this.mapService.getCoreMap();
  }

  isDiscreteEnabled(): boolean {
    return this.mapService.isDiscreteEnabled();
  }

  /**
   * Save coreMap
   * Capture moveend event
   * Update map status
   * @param coreMap
   */
  setCoreMap(coreMap: any): void {
    this.mapService.setCoreMap(coreMap);
    this.elementsService.setCoreMap(coreMap);
    const mapStatus = coreMap.getMapStatus(ViewerConstants.PROJECTIONS.WebMercator);
    if (mapStatus && mapStatus.center && !(Number.isNaN(mapStatus.center[0]) || Number.isNaN(mapStatus.center[1]))) {
      const viewExtent = GeoService.bboxFromPoints(mapStatus.extent);
      const center = GeoService.pointFromArray(mapStatus.center);

      this.mapService.updateMapStatus(center, viewExtent, mapStatus.zoom);
      this.setCenterCoordinates(this.getLocation());
    }

    coreMap.addListener("moveend", this.updateAfterMoveEnd.bind(this));
    // coreMap.addListener("pointerdrag", this.updateOnMove.bind(this));
    coreMap.addListener("discreteloaded", this.loadDiscreteFinished.bind(this));
    // coreMap.addListener("nodiscreteimages", this.noDiscreteImagesInPoint.bind(this));
    coreMap.addListener("clickfeatures", this.mapFeaturesClicked.bind(this));
    // coreMap.addListener("findimagesloaded", this.findImagesLoaded.bind(this));
    // coreMap.addListener("addedlayer", this.addedLayer.bind(this));
    coreMap.addListener("addfeaturecollection", this.addFeatureCollection.bind(this));
    coreMap.addListener("erroraddfeaturecollection", this.errorAddFeatureCollection.bind(this));
    coreMap.addListener("discreteoutimagelimits", this.outImageLimits.bind(this));
    coreMap.addListener("onmove", this.outImageLimits.bind(this));
    coreMap.addListener("startloadingtiles", this.startTilesLoading.bind(this));
    coreMap.addListener("endloadingtiles", this.endTilesLoading.bind(this));
    coreMap.addListener("zoomend", this.zoomEnd.bind(this));
    coreMap.addListener("unauthorized", this.setUnauthorized.bind(this));

    coreMap.addListener("loading:show", this.showLoadingComponent.bind(this));
    coreMap.addListener("loading:remove", this.hideLoadingComponent.bind(this));
    // coreMap.addListener("initialize", this.firstLoadFinished.bind(this));
    coreMap.addListener("map:start", this.firstLoadStart.bind(this));

    coreMap.addListener("measure:selected", this.onMeasuresSelected.bind(this));

    coreMap.addListener("measure:update", this.onMeasureUpdated.bind(this));

    coreMap.addListener("autodisastercontrast", this.onAutoDisasterContrast.bind(this));

    coreMap.addListener("measure:finished", this.onMeasureFinished.bind(this));

    coreMap.addListener("dragpanend", this.onDragPanEnd.bind(this));

    coreMap.addListener("pointdrawn", this.createPointInfoPopup.bind(this));

    coreMap.addListener("featuresLoaded", this.featuresLoaded.bind(this));

    coreMap.addListener("status:dsm", this.updateDSMStatus.bind(this));

    coreMap.addListener("layer:projects", this.onUpdateLayerProjects.bind(this));
    coreMap.addListener("map:collection:status", this.onUpdateCollectionStatus.bind(this));

    coreMap.addListener("map:collection:populated", this.onCollectionsPopulated.bind(this));
    coreMap.addListener("swipemoveend", this.onSwipeMoveEnd.bind(this));

    coreMap.addListener("contextmenu:centermap", this.onContextMenuCenterMapEnd.bind(this));

    coreMap.addListener("contextmenu:copycoordinates", this.onContextMenuCopyCoordinatesEnd.bind(this));

    coreMap.addListener("contextmenu:zoomin", this.onContextMenuCopyZoomIn.bind(this));

    coreMap.addListener("contextmenu:zoomout", this.onContextMenuCopyZoomOut.bind(this));

    coreMap.addListener("contextmenu:exportfeature", this.onContextMenuExportFeature.bind(this));

    coreMap.addListener("tool:relativeelevation", this.onRelativeElevation.bind(this));
  }

  setApiService(service: any) {
    this.apiService = service;
  }

  // region Layer visibility/order
  /**
   * Change overlay layer visibility by id
   * Update overlay layer raw data and update observable value
   * Change visibility on map layer
   * @param layerId
   * @param {boolean} visibility
   */
  changeLayerVisibility(layerId: string, visibility: boolean, changeLayers: boolean = true): void {
    this.overlayLayersRaw.forEach((item) => {
      if (item.id === layerId) {
        item.visible = visibility;
      }
      item.childs
        .filter((child) => child.id === layerId)
        .map((child) => {
          child.visible = visibility;
          return child;
        });
    });
    this.getCoreMap().changeVisibility(layerId, visibility);
    this.updateOverlayLayers(this.overlayLayersRaw, true);
    if (changeLayers) this.changeLayers();
  }

  /**
   * Change visibility in core map without any impact
   * @param id
   * @param visibility
   */
  changeVisibility(id: string, visibility: boolean) {
    this.mapService.changeVisibility(id, visibility);
  }

  changeCollectionVisibility(
    collectionId: string,
    visible: boolean,
    forceChildChange: boolean = true,
    forceUpdateProjects: boolean = true,
    updateInMap: boolean = false,
  ) {
    this.collectionLayersRaw
      .filter((collection) => collection.id.toString() === collectionId.toString())
      .map((collection) => {
        collection.visible = visible;
        collection.aois?.map((aoi) => {
          aoi.collection_visible = visible;
          if (forceChildChange) aoi.visible = visible;
          aoi.aois?.map((childAOI) => {
            if (forceChildChange) childAOI.visible = visible;
            childAOI.parent_visible = aoi.visible;
            childAOI.collection_visible = visible;
          });
          return aoi;
        });
        return collection;
      });
    if (forceUpdateProjects) {
      this.updateCollectionLayers(this.collectionLayersRaw);
      this.changeLayers();
    }
    if (updateInMap) {
      this.mapService.changeVisibility(collectionId, visible);
    }
  }

  changeAOIVisibility(
    layerId: string,
    visibility: boolean,
    forceChildChange: boolean = false,
    forceUpdateProjects: boolean = true,
  ) {
    const aoi = this.getLayerById(layerId);

    if (aoi) {
      aoi.visible = visibility;
      aoi.aois?.map((child) => {
        if (forceChildChange) {
          child.visible = visibility;
          this.mapService.changeVisibility(child.id, visibility);
        }
        child.parent_visible = visibility;
      });
      this.mapService.changeVisibility(aoi.id, visibility);
    }
    if (forceUpdateProjects) {
      this.updateCollectionLayers(this.getCollections());
      this.changeLayers();
    }
  }

  changeUserLayerVisibility(layerId: string, visibility: boolean): void {
    this.userLayersRaw.forEach((item) => {
      if (item.id === layerId) {
        item.visible = visibility;
        if (item.layerType === ViewerConstants.LAYER_TYPE.GROUPLAYER && item.childs?.length) {
          item.childs.forEach((child) => {
            child.parent_visible = visibility;
          });
        }
      }
    });
    this.mapService.changeVisibility(layerId, visibility);
  }

  /**
   * Update order in overlay layers
   * Receive overlaylayers ordered by drag and drop component
   * Update order value on each layer
   * Update zIndex parameter on map layer
   * @param {Viewerlayer[]} layers
   * @param silently
   */
  updateOverlayOrder(layers: Viewerlayer[], silently: boolean = false): void {
    const OVERLAY_OFFSET = 100000;
    layers.forEach((element, index) => {
      element.order = OVERLAY_OFFSET - index;
    });

    this.getCoreMap().updateOverlayOrder(layers);
    if (!silently) {
      this.changeLayers();
      this.saveUserConfiguration(this.getLayersToSaveConfiguration());
    }
  }

  updateUserOrder(layers: Viewerlayer[]): void {
    const OVERLAY_OFFSET = 150000;
    layers.forEach((element, index) => {
      element.order = OVERLAY_OFFSET - index;
    });

    layers.forEach((l) => {
      // Force update layer in IndexedDB
      this.geoJSONDatabaseService.updateLayer(l).toPromise();
    });

    this.getCoreMap().updateUserOrder(layers);
  }

  updateBaseLayers(layers: Viewerlayer[], silently = false): void {
    const OVERLAY_OFFSET = 0;
    layers.forEach((element, index) => {
      element.order = OVERLAY_OFFSET + index;
    });
    this.baseLayersRaw = layers;
    this.baseLayersSubject.next(this.baseLayersRaw);
    if (!silently) {
      this.changeLayers();
    }
  }

  /**
   * Launch event when change layers configuration change
   * Sort, order, visibility... anything related with the overlay, or base layers
   */
  changeLayers() {
    // why  need to update project status after change layers?? data is similar,
    // const projects = this.getCollectionProjectLayers().map((p) => ProjectService.processProject(p));

    // maybe just enough force this for update status and trigger subject
    this.mapService.getOverlayManager().triggerMapStatus();
    this.updateCenterImageDate();
    this.updateLocationInfo();
    // we have to update the url state after any layer change, only when the map its initialized
    this.updateUrlParameters();
  }

  updateOverlayLayers(layers: Viewerlayer[], silently = false): void {
    this.overlayLayersRaw = layers;
    this.overlayLayersSubject.next(this.overlayLayersRaw);
    if (!silently) {
      this.changeLayers();
    }
  }

  updateUserLayers(layers: Viewerlayer[]): void {
    this.userLayersRaw = [].concat(layers.sort((l1, l2) => l2.order - l1.order)); // force trigger event
    this.userLayersSubject.next(this.userLayersRaw);
  }

  updateSelectedBaseLayer(layer: Viewerlayer): void {
    this.selectedBaseLayerRaw = layer;
    this.selectedBaseLayerSubject.next(this.selectedBaseLayerRaw);
    this.updateLocationInfo();
  }

  // endregion

  updateLoadedImages(images: IMetadataImage[]): IHistoryProject[] {
    this.setReportReady(!!images?.length);
    if (images?.length) {
      images.map((img) => {
        img.capturedDate = new Date(img.capture_date);
        return img;
      });
    }

    this.loadedImagesSubject.next(images);
    const projects = ImagesService.projectsFromImages(images);
    this.loadedProjectsSubject.next(projects);
    return projects;
  }

  getLoadedProjects(): IHistoryProject[] {
    return this.loadedProjectsSubject.getValue();
  }

  /**
   * Call to server to save updated data
   */

  saveUserConfiguration(customLayers: Viewerlayer[] = []) {
    const reducedLayers = LayerUtilsService.reduceLayers(
      customLayers,
      this.getZoom() >= ViewerConstants.COLLECTION_TIMELINE_ZOOM_LEVEL,
    );
    const reduced = StatusService.reduceStateLayers(reducedLayers);
    const location = this.getLocation();
    const filters = this.getSimplifiedFilters();
    const readNotifications = this.getReadNotifications();
    const userConfiguration: IUserConfigurationRequest = {
      instanceId: this.getInstanceId(),
      lastLocation: location ? GeoService.pointToWkt(this.getLocation()) : null, // by default geographic
      lastZoom: this.getZoom(),
      measureSystem: this.getMeasurementSystemId(),
      measureUnit: this.getMeasurementLengthUnit(),
      measureLengthUnit: this.getMeasurementLengthUnit(),
      measureAreaUnit: this.getMeasurementAreaUnit(),
      measurePitchUnit: this.getMeasurementSlopeUnit(),
      propertyInfoUnit: this.getPropertyInfoUnit(),
      layers: reduced,
      filters: filters,
      viewedNotifications: readNotifications,
      initialData: StatusService.getInitialData(this.getProjectStatus()?.centerAOI),
    };
    this.userConfigCommunicatorService.saveUserConfigurationRequest(userConfiguration);
  }

  getLayersToSaveConfiguration(): Viewerlayer[] {
    return this.getCollections().concat(this.getOverlayLayers()).concat(this.getBaseLayers());
  }

  // endregion

  createMeasurementTools(toolTypeIds: number[]): void {
    const measurementTools: MeasurementTool[] = [];
    for (let i = 0; i < toolTypeIds.length; i++) {
      measurementTools.push(
        this.measurementsToolService.createMeasurementTool(
          this.getCoreMap(),
          toolTypeIds[i],
          this.mapService.mapIndex,
          this.getMeasurementSystemId(),
          this.getMeasurementLengthUnit(),
          this.getMeasurementAreaUnit(),
        ),
      );
    }

    this.getCoreMap().addTools(measurementTools.map((tool) => tool.internalTool));
    this.updateMeasurementTools(measurementTools);
  }

  // region Project filters/ mask
  getSimplifiedFilters(): ITimelineFilter {
    const filters = { ...this.getTimelineFilters() };
    try {
      if (filters) {
        return {
          imageryTypes: filters.imageryTypes?.map((f) => FilterProjectService.reduce(f)) || [],
          modeTypes: filters.modeTypes?.map((f) => FilterProjectService.reduce(f)) || [],
          orthoStatus: filters.orthoStatus?.map((f) => FilterProjectService.reduce(f)) || [],
          resolutions: filters.resolutions?.map((f) => FilterProjectService.reduce(f)) || [],
          layerTypes: filters.layerTypes?.map((f) => FilterProjectService.reduce(f)) || [],
        };
      }
    } catch (err) {
      console.debug("Error simplifying filters");
    }
    return filters;
  }

  saveTimelineFilters(filters: ITimelineFilter) {
    this.setTimelineFilters(filters);
    this.createStateParameter();
  }

  //TODO: THIS TRIGGER CHANGE DETECTION
  updateUrlParameters(state: boolean = true) {
    this.ngZone.run(() => {
      if (this.mapService.isMapInitialized()) {
        let params = {};
        this.setCenterCoordinates(this.getLocation());
        if (this.mapService.isMasterMap()) {
          params = this.urlStateService.setLocationURL(
            this.getCoreMap()?.getCenter(ViewerConstants.PROJECTIONS.WGS84),
            this.getCoreMap()?.getZoom(),
          );
        }
        if (state) {
          this.createStateParameter();
          const stateParam = this.urlStateService.setStateURL(this.mapService.getMapId());
          if (stateParam) {
            params = { ...params, ...stateParam };
          }
        }

        this.urlStateService.updateQueryParams(params);
      }
    });
  }

  createStateParameter(): any {
    return this.urlStateService.createStateParameter(
      this.getProjectStatus(),
      this.getCoreMap().getMode(),
      this.orientationSelected,
      this.getOverlayLayers(),
      this.getSimplifiedFilters(),
      this.isActivatedBaseMap(),
      this.mapService.getMapId(),
      this.getInfraredStatus(),
      this.getGlobalMeasurementSystem(),
    );
  }

  setTimelineFilters(filters: ITimelineFilter) {
    this.timelineFiltersSubject.next(filters);
  }

  /**
   * Filter projects by filter criteria
   * Return object with list of filtered projects  and list of hidden projects
   * @param filter
   */
  filterProjectsByFilter(filter: ITimelineFilter): any {
    const projectStatus = this.getProjectStatus();

    const projects = projectStatus?.aois;
    const filtered = FilterProjectService.filterProjects(projects, filter);

    const filteredNames = filtered?.map((f) => f.getName());
    const hidden = projects?.filter((p) => !filteredNames.includes(p.getName()));

    return {
      visible: filtered,
      hidden: hidden,
    };
  }

  /**
   * Apply timeline filters
   * @param filter  filter object itself
   * @param visible list of visible projects after apply filter
   * @param hidden  list of hidden projects after apply filter
   */
  applyTimelineFilter(filter: ITimelineFilter, visible: IProjectAOI[], hidden: IProjectAOI[]) {
    this.setTimelineFilters(filter);

    const projectStatus = this.getProjectStatus();
    //todo remove this part because it´s for collection projects logic
    if (projectStatus?.multiLayerEnabled) {
      this.applyCollectionsFilters(filter);
    } else {
      this.applyProjectsFilters(filter, visible, hidden);
    }
  }

  /**
   * @deprecated
   * Method that updates filters and collections with the collection layer + projects information
   */
  applyProjectsFilters(filter: ITimelineFilter, visible: IProjectAOI[], hidden: IProjectAOI[]) {
    const projectStatus = this.getProjectStatus();
    const previousAOI = projectStatus?.centerAOI ? { ...projectStatus?.centerAOI } : null;
    const layerProjects = ProjectService.groupByLayer(visible);
    //iterate trough imagery type and change visibility for collections
    this.getCollections().forEach((c) => {
      const projects = layerProjects.get(c.layer) || [];
      this.updateCollectionMask(c.id, projects);
      filter?.imageryTypes.forEach((type) => {
        if (c.collectionType === type.value && type.checked !== c.visible) {
          this.changeCollectionVisibility(c.id, type.checked, false, false, true);
        }
      });
    });

    if (visible?.length) {
      visible.forEach((p) => {
        if (!p.isVisible()) {
          this.changeVisibility(p.getLayerId(), true);
          //todo change this to allow new collection type
          if (p.layer) {
            p.layer.visible = true;
          }
        }
      });
    }
    if (hidden?.length) {
      hidden.forEach((p) => {
        if (p.isVisible()) {
          this.changeVisibility(p.getLayerId(), false);
          //todo change this to allow new collection type
          if (p.layer) {
            p.layer.visible = false;
          }
        }
      });
    }

    //here we have to calculate again center project aois etc to refresh image data blabla
    this.refreshCollectionLayers();

    //now we have to check if anything change for projects
    const currentAOI = this.getProjectStatus()?.centerAOI;

    if (this.is3DMode()) {
      if (currentAOI?.getLayerId() !== previousAOI?.getLayerId()) {
        this.show3DProject(currentAOI);
      }
    } else if (
      currentAOI?.getLayerId() !== previousAOI?.getLayerId() &&
      previousAOI?.isVisible() &&
      previousAOI.isEnabled()
    ) {
      this.show2DProject(previousAOI);
    }
  }

  applyCollectionsFilters(filter: ITimelineFilter) {
    if (this.is3DMode()) {
      const projectStatus = this.getProjectStatus();
      const centerAOI = projectStatus.centerAOI;
      const visibleAOIFromFilters = projectStatus.enabledAOIs.filter(
        (aoi) => aoi.isEnabled3D() && FilterProjectService.filterProject(aoi, this.getTimelineFilters()),
      );

      if (!visibleAOIFromFilters.find((vp) => vp.getLayerId() === centerAOI?.getLayerId())) {
        const topVisible = visibleAOIFromFilters[visibleAOIFromFilters.length - 1];
        this.mapService
          .getOrientedManager()
          .loadDiscreteByProject(topVisible?.getLayerName(), topVisible?.getLayerId());
      } else {
        this.updateTimelineFilters();
      }
    } else {
      this.mapService.getOverlayManager().updateVexcelSources(
        filter.layerTypes
          .filter((f) => f.checked)
          .map((f) => f.value.split(","))
          .flat(),
        filter.layerTypes
          .filter((f) => !f.checked)
          .map((f) => f.value.split(","))
          .flat(),
      );
    }

    const grayskyUnchecked = filter.layerTypes.some((layer) => layer.title === "Graysky" && layer.checked === false);
    if (grayskyUnchecked) this.toggleEnhance(false);
  }

  // endregion

  updateMeasurementTools(tools: MeasurementTool[]): void {
    this.measurementToolsSubject.next(tools);
  }

  updateLockedDiscreteMode() {
    const lockedDiscreteMode = this.mapService.isDiscreteEnabled()
      ? this.getCoreMap().checkLockedDiscreteMode()
      : false;
    this.lockImageModeSubject.next(lockedDiscreteMode);
  }

  removeOverlayLayer(id: string) {
    this.layerService.removeLayerById(id, this.getCoreMap());
    const layers = this.getOverlayLayers().filter((layer) => String(layer.id) !== String(id));
    this.updateOverlayLayers(layers);
  }

  shareOverlayLayer(id: string) {
    this.overlayLayersRaw.find((layer) => String(layer.id) === String(id)).owner = false;
    this.updateOverlayLayers(this.overlayLayersRaw);
  }

  /**
   * Update overlay styles on map
   */
  updateOverlayStyles(): void {
    this.getCoreMap().updateOverlayStyles(this.getOverlayLayers());
  }

  /**
   * Transform mercator coordinates to pixel
   * @param {number[]} coordinates in mercator
   * @returns {number[]} pixels
   */
  coordinatesToPixel(coordinates: number[][]): number[][] {
    return coordinates.map((coordinate) =>
      this.getCoreMap().coordinateToPixel(coordinate, ViewerConstants.PROJECTIONS.WebMercator),
    );
  }

  allowExportGeoTiff() {
    this.enableExportGeoTIFFSubject.next(this.getCoreMap().isExportGeoTIFFAllowed());
  }

  allowMeasurements() {
    this.enableMeasurementsSubject.next(this.mapService.allowMeasurements());
  }

  allowProperties() {
    this.enablePropertiesSubject.next(this.mapService.allowProperties());
  }

  allowPropertiesReport() {
    this.enablePropertiesReportSubject.next(this.mapService.allowPropertiesReport());
  }

  deactivateTools(value = true) {
    this.deactivateToolsSubject.next(value);
  }

  zoomToFeature(layer: Viewerlayer, feature: any) {
    // close before/after if is opened
    const centerPoint = GeoService.pointToMercator(this.getCenterCoordinates());
    if (
      this.mapService.swipeStatus.enable &&
      !GeoService.isCenterEquals(centerPoint.geometry.coordinates, feature.geometry.coordinates)
    ) {
      this.searchEnd({ search: true });
    }

    // we pass the fit options
    // for features we add a 10 px padding
    // we always send the duration 0
    const fitOptions = {
      padding: [10, 10, 10, 10],
      duration: 0,
    };
    this.getCoreMap()?.zoomToFeature(layer.id, feature.id, fitOptions);
  }

  rotateView(orientation: string) {
    const orientationObject = ViewerConstants.COLLECTION_MODES.VERTICAL.orientations[orientation];
    if (orientationObject) {
      this.getCoreMap().rotateView(orientationObject.name, orientationObject.azimut);
    }
  }

  clickGetInfoButton(coordinates: number[], features?: any) {
    this.showLocationInfo(coordinates, features);
  }

  toggleContrastEnhance(layerId: string) {
    const selectedLayer = this.getLayerById(layerId);

    if (!selectedLayer) {
      return;
    }

    selectedLayer.enableContrast = !selectedLayer.enableContrast;

    this.getCoreMap().toggleContrastEnhance(layerId, selectedLayer.enableContrast);
  }

  toggleEnhance(enable: boolean) {
    const vexcelBaseLayer = this.getCoreMap().getOverlayManager().getVexcelBaseLayer();
    if (vexcelBaseLayer) {
      this.getCoreMap().toggleContrastEnhance(null, enable);
      this.enableContrastSubject.next(enable);
    } else {
      this.getProjectStatus()
        .aois?.filter((a) => a.isGraySky() && a.hasImages())
        .forEach((a) => {
          if (enable !== a.layer.enableContrast) {
          }
          this.toggleContrastEnhance(a.getLayerId());
        });
      this.getCollections()
        .filter((c) => c.collectionType === ViewerConstants.COLLECTION_PREFIX.GRAYSKY)
        .forEach((c) => {
          if (enable !== c.enableContrast) {
            this.toggleContrastEnhance(c.id);
          }
        });
    }
  }

  getInfraredStatus(): boolean {
    //check infrared status depends of mode
    if (this.isDiscreteEnabled()) {
      if (this.getSelectedImage()) {
        return this.getSelectedImage().orientation === ViewerConstants.DISCRETE_ORIENTATIONS.I;
      } else {
        return false;
      }
    } else {
      return this.infraredStatusSubject.getValue();
    }
  }

  getPinColor(): string {
    return this.getInfraredStatus() ? ViewerConstants.INFRARED_PIN_COLOR : ViewerConstants.PIN_COLOR;
  }

  createPointInfoPopup(event: any) {
    this.showPointInfoPopup(event.point, event.feature, event.epsg);
  }

  showPointInfoPopup(coordinates: number[], features: any, epsg: string) {
    const pointInfoPopup: IPopup = {
      coordinates,
      featuresData: features,
      epsg,
      type: "point-info-popup",
    };
    this.pointInfoPopupSubject.next(pointInfoPopup);
  }

  /**
   * Remove address from params
   * hidden infopopup
   * saving address url param to null
   * to avoid fail in next search
   */
  closeAddressPopup() {
    this.removeSelectedInfoPopup();
    this.getCoreMap().removeRedPinMarkers();
    this.infoPopupSubject.next(null);
    this.urlStateService.getSavedUrlParams().address = null;
    this.urlStateService.updateQueryParams({ address: null });
  }

  updateLocationInfo() {
    const infoPopup: IPopup = {
      coordinates: null,
      epsg: ViewerConstants.PROJECTIONS.WGS84,
      type: "info-popup",
    };

    this.updateLocationInfoSubject.next(infoPopup);
  }

  updatePointInfoPopup(coordinates: number[], feature: any) {
    const pointInfoPopup: IPopup = {
      coordinates,
      epsg: ViewerConstants.PROJECTIONS.WGS84,
      type: "point-info-popup",
      featuresData: feature,
    };
    this.updatePointInfoPopupSubject.next(pointInfoPopup);
  }

  movePointInfoPopup(coordinates: number[], epsg, featureId: string) {
    this.mapService.getCoreMap().moveOverlayPopUpById(featureId, coordinates, epsg);
  }

  closePointInfoPopups() {
    this.pointInfoPopupSubject.next(null);
    this.getCoreMap().removeOverlayPopUp("point-info-popup");
  }

  removePointInfoPopupForLastMeasurementFeature() {
    this.getCoreMap().removePointInfoPopupForLastMeasurementFeature();
  }

  /**
   * Enable/disable reproject vector data between orientations
   * @param reproject
   */
  reprojectObliques(reproject: boolean) {
    return this.getCoreMap().reprojectInObliques(reproject);
  }

  showPolygonTriangulation(show: boolean) {
    const debugParameters = this.getCoreMap().getDebugParameters();
    debugParameters.paintTriangulation = show;
    this.getCoreMap().setDebugParameters(debugParameters);
  }

  // region MapStatus && ProjectStatus
  /**
   * Get newest image date for center of the view extent
   */
  updateCenterImageDate() {
    let centerImage;
    if (this.mapService.isDiscreteEnabled() && this.getSelectedImage()) {
      centerImage = this.getSelectedImage();
    } else {
      const aoi = this.getProjectStatus()?.centerAOI;
      centerImage = aoi?.getNewestImage(ImagesService.DEFAULT_NADIR) || null;
    }
    let imageDate = ImagesService.formatImageDate(centerImage);

    // if we dont have images for this point we get the image from aoi
    if (!imageDate && this.getProjectStatus()?.centerAOI && !this.getProjectStatus()?.centerAOI?.images?.length) {
      imageDate = this.getProjectStatus().centerAOI.getMaxDate("MMMM Do, YYYY");
    }

    this.setCenterImageDate(imageDate);
  }

  updateProjectStatus(
    projects: IProjectAOI[],
    collection?: string,
    mapStatus: MapStatus = this.mapService.getMapStatus(),
  ): ProjectStatus {
    // update project with map zIndex for sorting
    const overlayManager = this.mapService.getOverlayManager();
    projects?.forEach((prj) => {
      const aoi = overlayManager.findAOI(prj.getName());
      if (aoi) {
        prj.layer.order = aoi.getZIndex();
      }
    });

    const status = new ProjectStatus(projects, mapStatus, collection);
    status.multiLayerEnabled = this.getCollections()?.length === 0;

    status.enabledAOIs.map((aoi) => aoi.setVisible(FilterProjectService.filterProject(aoi, this.getTimelineFilters())));

    this.projectStatusSubject.next(status);
    this.projectStatusTriggerSubject.next(status);
    return status;
  }

  // endregion
  setNorthUpOrientation() {
    this.getCoreMap().setNorthUpOrientation(this.getCollections().length ? this.getCollections()[0].id : null);
  }

  setDefaultLocation() {
    if (this.is3DMode()) {
      this.changeMode();
      this.mapService.fitToExtent(
        GeoService.bbox(this.getCoreMap().getDefaultLocation()),
        ViewerConstants.PROJECTIONS.WGS84,
      );
      this.viewerConfigurationService.setDefaultLocation(false);
    } else if (!this.is3DMode() && this.orientationSelected !== ViewerConstants.DISCRETE_ORIENTATIONS.N) {
      this.setNorthUpOrientation();
    } else {
      this.mapService.fitToExtent(
        GeoService.bbox(this.getCoreMap().getDefaultLocation()),
        ViewerConstants.PROJECTIONS.WGS84,
      );
      this.viewerConfigurationService.setDefaultLocation(false);
    }
  }

  setDefaultFeaturesStyle(features) {
    features?.forEach((feature) => {
      const featureLayers = this.getCoreMap().getLayersByName(feature.layerName);
      if (featureLayers) {
        featureLayers.forEach((layer) => {
          if (layer.get("layerType") === ViewerConstants.SOURCE_NAME.VECTOR) {
            const oldSelectedFeature = layer
              .getSource()
              .getFeatures()
              .find((f) => f.get("selected"));
            if (oldSelectedFeature) {
              oldSelectedFeature.set("selected", false);
              oldSelectedFeature.setStyle(null);
            }
          }
        });
      }
    });
  }

  setSelectedFeatureStyle(features) {
    features?.forEach((feature) => {
      if (feature) {
        feature.selected = true;
        const featureLayers = this.getCoreMap()?.getLayersByName(feature.layerName);
        if (featureLayers) {
          featureLayers.forEach((layer) => {
            if (layer.get("layerType") === ViewerConstants.SOURCE_NAME.VECTOR) {
              const featureById = layer.getSource().getFeatureById(feature.id);
              if (featureById && feature.geometry.type === ViewerConstants.GEOMETRY_TYPE.Point) {
                featureById.set("selected", true);
                if (layer.getStyle().image_)
                  this.getCoreMap().setStyleFeature(featureById, {
                    src: layer.get("overlayProperties").properties.iconSrc,
                    scale: 1.5,
                  });
              }
            }
          });
        }
      }
    });
  }

  removeInfoProperty(infoProperty: IPopup) {
    if (infoProperty.featuresData && infoProperty.featuresData[0].selected) {
      this.setDefaultFeaturesStyle(infoProperty.featuresData);
    }
    const index = this.infoPropertiesRaw.indexOf(infoProperty);
    this.infoPropertiesRaw.splice(index, 1);
    this.setInfoProperties(this.infoPropertiesRaw);
    if (index === 0) {
      this.urlStateService.updateQueryParams({ address: null });
    }
    if (infoProperty.property) {
      this.mapService.removePropertyFootprint(infoProperty.property.feature_id);
      infoProperty.property.structures?.forEach((structure) => {
        this.mapService.removePropertyFootprint(structure.feature_id);
        this.mapService.removePropertyFootprint(structure.label_id);
      });
    }
    if (infoProperty.damageAssessment) {
      this.mapService.removeDamagePropertyFootprint(infoProperty.damageAssessment.pre_catastrophe.feature_id);
      infoProperty.damageAssessment.pre_catastrophe?.structure?.forEach((structure) => {
        this.mapService.removeDamagePropertyFootprint(structure.feature_id);
        this.mapService.removeDamagePropertyFootprint(structure.label_id);
      });
    }
  }

  showLocationInfo(coordinates: number[], features: any) {
    const infoLocation: IPopup = {
      coordinates,
      epsg: ViewerConstants.PROJECTIONS.WGS84,
      type: "info-popup",
      featuresData: features,
      opts: {
        icon: {
          src: "assets/images/small_pin.svg",
          color: this.getPinColor(),
        },
      },
      selected: true,
      onClose: this.closeAddressPopup.bind(this),
    };
    this.setSelectedFeaturesStyle(features);
    this.infoPropertiesRaw.forEach((info) => {
      if (info.featuresData) {
        info.featuresData[0].selected = false;
      }
      info.selected = false;
    });
    this.infoPropertiesRaw.unshift(infoLocation);
    this.setInfoProperties(this.infoPropertiesRaw);
  }

  removeSelectedInfoPopup() {
    this.infoPropertiesRaw = this.infoPropertiesRaw.filter((info) => !info.selected);
    this.setInfoProperties(this.infoPropertiesRaw);
  }

  getInfoProperties(): IPopup[] {
    return this.infoPropertiesRaw;
  }

  setInfoProperties(infoPopups: IPopup[]) {
    this.infoPropertiesRaw = [].concat(infoPopups); // Necessary to trigger the setter of the @Input
    this.infoPropertiesSubject.next(this.infoPropertiesRaw);
  }

  setSelectedFeaturesStyle(features) {
    if (this.selectedFeaturesSubject.getValue()) this.setDefaultFeaturesStyle(this.selectedFeaturesSubject.getValue());
    this.setSelectedFeatureStyle(features);
  }

  setSelectedFeatures(features: any) {
    this.selectedFeaturesSubject.next(features);
  }

  getSelectedFeatures(): any {
    return this.selectedFeaturesSubject.getValue();
  }

  countGeometryTilesForZoom(geometry: any, zoom: number) {
    return this.getCoreMap().getTilesFromGeometryAndZoom(geometry, zoom);
  }

  enableContextMenu() {
    this.regenerateContextMenuItems();
    this.enableContextMenuDefaultItems();
    this.enableGetInfoContextMenu();
    this.enableExportGeoTIFFContextMenu();
  }

  regenerateContextMenuItems() {
    this.getCoreMap()?.regenerateContextMenu();
  }

  enableContextMenuDefaultItems() {
    this.getCoreMap()?.getContextMenu().enableDefaultItems();
  }

  /**
   * Adds a context menu item that triggers the "Get Info" functionality.
   */
  enableGetInfoContextMenu() {
    const allowed = this.featureFlagService.hasFlag(FeatureFlags.PROPERTIES);
    if (allowed) {
      this.addContextMenuItem({
        text: "Get Info",
        callback: (obj: any, _) => {
          // Ensure the callback runs within Angular's zone
          this.ngZone.run(() => this.clickGetInfoButtonCallback(obj, _));
        },
      });
    }
  }

  clickGetInfoButtonCallback(obj: any, _) {
    const featureMercator = GeoService.pointToMercator(obj.coordinate);
    const pixel = this.coordinatesToPixel([featureMercator.geometry.coordinates]);
    const featuresClicked = this.mapService
      .getCoreMap()
      .getFeaturesClicked(pixel[0], featureMercator.geometry.coordinates);

    let features = featuresClicked?.length > 0 ? featuresClicked : null;

    this.clickGetInfoButton(obj.coordinate, features);
    this.googleAnalyticsService.setGoogleAnalyticsEvent(
      GoogleAnalyticsConstantsEvents.RIGHT_CLICK_GET_INFO,
      GoogleAnalyticsConstantsCategory.CONTEXT_MENU,
      true,
    );
  }

  enableExportGeoTIFFContextMenu() {
    const allowed =
      this.enableExportGeoTIFFSubject.getValue() &&
      !this.mapService.isDiscreteEnabled() &&
      !this.mapService.swipeStatus.enable &&
      this.featureFlagService.hasFlag(FeatureFlags.EXPORT_GEOTIFF);

    if (allowed)
      this.addContextMenuItem({
        text: "Export GeoTIFF",
        callback: this.clickExportGeoTIFF.bind(this),
      });
  }

  addContextMenuItem(item: any) {
    this.getCoreMap()?.getContextMenu().addItem(item);
  }

  clearContextMenu() {
    this.getCoreMap()?.getContextMenu().clearMenu();
  }

  clickExportGeoTIFF() {
    this.toolbarService.enableTool(toolbarOptions.EXPORT_GEOTIFF);
  }

  setActivatedBaseMap(activated: boolean) {
    this.isActivatedBaseMapSubject.next(activated);
  }

  isActivatedBaseMap() {
    return this.isActivatedBaseMapSubject.getValue();
  }

  /**
   * Call to coremap function to refresh collection mask, based on collection aois visibility
   */
  updateCollectionMask(id: number, projects: IProjectAOI[]) {
    this.getCoreMap().refreshCollectionMask(
      id.toString(),
      projects.map((p) => p.getName()),
    );
  }

  // region Get/Set  Allow/ properties

  getMeasurementTools(): MeasurementTool[] {
    return this.measurementToolsSubject.getValue();
  }

  getTimelineFilters(): ITimelineFilter {
    return this.timelineFiltersSubject.getValue();
  }

  /**
   * Reset all timeline filters to their default values (checked = true)
   */
  resetTimelineFilters(): void {
    const currentFilters = this.getTimelineFilters();

    // Determine which type of filter to use based on the current filters
    let newFilters: ITimelineFilter;
    if (currentFilters?.layerTypes && !currentFilters?.modeTypes) {
      // Collection filter
      newFilters = FilterProjectService.emptyCollectionFilter();
    } else {
      // Projects filter
      newFilters = FilterProjectService.emptyProjectsFilter();
    }

    // Filter projects based on the new filters
    const projects = this.filterProjectsByFilter(newFilters);

    // Apply the filters to the projects
    this.applyTimelineFilter(newFilters, projects?.visible, projects?.hidden);
  }

  setInstanceId(id: number): void {
    this._instanceId = id;
  }

  getInstanceId(): number {
    return this._instanceId;
  }

  setReadNotifications(notifications: string[]) {
    this.readNotificationsSubject.next(notifications);
  }

  getReadNotifications(): string[] {
    return this.readNotificationsSubject.getValue();
  }

  getLoadedImages(): IMetadataImage[] {
    return this.loadedImagesSubject.getValue();
  }

  getSelectedImage() {
    return this.imageSubject.getValue();
  }

  setCenterCoordinates(location: number[]): void {
    this.mapCenterCoordinatesSubject.next(location);
  }

  getCenterCoordinates(): number[] {
    return this.mapCenterCoordinatesSubject.getValue();
  }

  setUnauthorized() {
    this.viewerConfigurationService.setUnauthorized();
  }

  setFilterMask(value: any) {
    this.getCoreMap().setFilterMask(value);
  }

  setZoom(zoom: number) {
    this.mapService.setZoom(zoom);
  }

  getInfoPopup(): IPopup {
    return this.infoPopupSubject.getValue();
  }

  setCenterImageDate(date: string) {
    this.centerImageDateSubject.next(date);
  }

  setReportReady(value: boolean) {
    this.reportReadySubject.next(value);
  }

  getReportReady(): boolean {
    return this.reportReadySubject.getValue();
  }

  getMeasurementSystemId(): number {
    return this.measurementSystemIdSubject.getValue();
  }

  getMeasurementLengthUnit(): string {
    return this.measurementLengthUnitSubject.getValue();
  }

  getMeasurementAreaUnit(): string {
    return this.measurementAreaUnitSubject.getValue();
  }

  getMeasurementSlopeUnit(): string {
    return this.measurementSlopeUnitSubject.getValue();
  }

  /**
   * Get actual map location by default in geographic
   * @returns {Point}
   * @param epsg
   */
  getLocation(epsg?: string): any {
    if (!this.getCoreMap()) {
      return null;
    }
    if (epsg) {
      return this.getCoreMap().getCenter(epsg);
    }
    return this.getCoreMap().getCenter();
  }

  /**
   * Get actual map zoom level
   * @returns {number}
   */
  getZoom(): number {
    return this.getCoreMap()?.getZoom() || null;
  }

  /**
   * Get overlay layers
   * @returns {Viewerlayer[]}
   */
  getOverlayLayers(): Viewerlayer[] {
    return this.overlayLayersSubject.getValue();
  }

  getInitLayers(): Viewerlayer[] {
    return this.initLayersSubject.getValue();
  }

  setInitLayers(layers: Viewerlayer[]) {
    this.initLayersSubject.next(layers);
  }

  setInitAoi(aoi: string) {
    this.initAoi = aoi;
  }

  getUserLayers(): Viewerlayer[] {
    return this.userLayersSubject.getValue();
  }

  getSelectedBaseLayer(): Viewerlayer {
    return this.selectedBaseLayerSubject.getValue();
  }

  /**
   * Get base layers
   * @returns {Viewerlayer[]}
   */
  getBaseLayers(): Viewerlayer[] {
    return this.baseLayersSubject.getValue();
  }

  /**
   * @deprecated
   * Old logic for get collection layers that can be misunderstood with API2 collections
   */
  getCollections(): Viewerlayer[] {
    return this.collectionLayersSubject.getValue();
  }

  getProjectStatus(): ProjectStatus {
    return this.projectStatusSubject.getValue();
  }

  setSelectedAoi(aoi: IProjectAOI) {
    this.selectedAoiSubject.next(aoi);
  }

  get orientationSelected(): string {
    return this._orientationSelected;
  }

  set orientationSelected(orientation: string) {
    this._orientationSelected = ImagesService.normalizeOrientation(orientation);
  }

  set collectionMode(mode: any) {
    this._collectionMode = mode;
  }

  get collectionMode(): any {
    return this._collectionMode;
  }

  // endregion

  removeFeatureFootprints(features: any) {
    features?.forEach((f) => {
      if (f.propertyInfo) {
        this.mapService.removePropertyFootprint(f.propertyInfo.feature_id);
        f.propertyInfo.structures.forEach((s) => {
          this.mapService.removePropertyFootprint(s.feature_id);
        });
      }
      if (f.damageInfo) {
        this.mapService.removeDamagePropertyFootprint(f.damageInfo.feature_id);
        f.damageInfo.structures.forEach((s) => {
          this.mapService.removeDamagePropertyFootprint(s.feature_id);
        });
      }
    });
  }

  getMapService(): MapService {
    return this.mapService;
  }

  setEmbeddingImplementation(implementation: string) {}
}
